import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Empty, Modal, Select, Spin, Table, Tag } from 'antd';
import { useHistory } from 'react-router-dom';
import {
  AlertOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FireOutlined,
  HomeOutlined,
  ShopOutlined,
  AppstoreOutlined,
  StopOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  differenceInCalendarDays,
  format,
} from 'date-fns';
import get from 'lodash.get';
import { useDispatch, useSelector } from 'react-redux';
import SubPageLayout from '../helpers/SubPageLayout';
import { GET } from '../../../service/base-api-service';
import { getUser } from '../../../features/auth.store';
import { getPaginatedSiteList, getSiteList } from '../../../features/site.store';
import {
  clearGasSensorExpiry,
  fetchGasSensorExpiryLog,
  getGasSensorExpiryData,
  getGasSensorExpiryError,
  getGasSensorExpiryLoading,
} from '../../../features/gas-sensor-expiry.store';
import { ROLE } from '../../../utils/features';
import './styles/support-dashboard.scss';

const API_BASE_URL = String(process.env.REACT_APP_BASE_URL || 'http://192.168.2.37:8090').replace(/\/+$/, '');
const CHAMBER_FAULTS_ENDPOINT = (chamberId) => `v1/report/dashboard/chambers/${chamberId}/faults`;
const getSseAccessToken = () =>
  localStorage.getItem('access_token') ||
  localStorage.getItem('token') ||
  localStorage.getItem('accessToken') ||
  sessionStorage.getItem('access_token') ||
  '';

const getFaultSummaryKey = (row, tenantId, siteId) =>
  `${tenantId || ''}:${siteId || ''}:${firstDefined(row, ['chamberId', 'id', 'chamberName'], '')}`;

const normalizeRoles = (user) => {
  const scope = get(user, 'scope');
  const role = get(user, 'role');
  const roles = get(user, 'roles', []);
  const authorities = get(user, 'authorities', []);
  const authorityNames = Array.isArray(authorities)
    ? authorities.map((a) => (typeof a === 'string' ? a : a?.authority)).filter(Boolean)
    : [];

  const splitScope = typeof scope === 'string' ? scope.split(',').map((s) => s.trim()).filter(Boolean) : [];

  return Array.from(new Set([...splitScope, ...(role ? [role] : []), ...(Array.isArray(roles) ? roles : []), ...authorityNames]));
};

const hasRole = (user, roleConst) => normalizeRoles(user).includes(roleConst);

const getTenantIdentifierFromUser = (user) =>
  get(user, 'tenant.identifier') ||
  get(user, 'tenant.id') ||
  get(user, 'tenantIdentifier') ||
  get(user, 'tenant_identifier') ||
  get(user, 'tenantId') ||
  '';

const getTenantNameFromUser = (user) =>
  get(user, 'tenant.name') || get(user, 'tenantName') || get(user, 'tenant_name') || '';

const normalizeSiteLabel = (value) => String(value || '').trim().toLowerCase();

const toSiteIdentifier = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? String(value).trim() : numeric;
};

const normalizeSiteIdentifier = (value) => {
  const normalized = toSiteIdentifier(value);
  return normalized === null ? '' : String(normalized);
};

const STAGE_COLORS = ['#e4413d', '#ff9f1c', '#23b26d', '#7b3fe4'];
const GAS_FAMILY_BUCKETS = [
  { key: 'overdue', label: 'Overdue', color: '#e1554f' },
  { key: 'due30', label: 'Due 30d', color: '#f0a436' },
  { key: 'upToDate', label: 'Up to Date', color: '#37b26f' },
  { key: 'noRecord', label: 'No Sensor records', color: '#8ea3b7' },
];

const polarToCartesian = (cx, cy, r, angleDeg) => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const describeArc = (cx, cy, r, startAngle, endAngle) => {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
};

const DonutChart = ({ data, total }) => {
  const safeTotal = Math.max(1, total);
  let angle = 0;

  return (
    <svg viewBox="0 0 220 220" className="donut-svg" role="img" aria-label="Chamber state distribution">
      <circle cx="110" cy="110" r="72" fill="none" stroke="#edf2f8" strokeWidth="24" />
      {data.map((slice, idx) => {
        if (!slice.count) return null;
        const sweep = (slice.count / safeTotal) * 360;
        if (sweep >= 359.999) {
          return (
            <circle
              key={slice.label}
              className="donut-segment"
              cx="110"
              cy="110"
              r="72"
              pathLength="100"
              style={{ animationDelay: `${380 + idx * 180}ms` }}
              fill="none"
              stroke={STAGE_COLORS[idx % STAGE_COLORS.length]}
              strokeWidth="24"
            />
          );
        }
        const path = describeArc(110, 110, 72, angle, angle + sweep);
        angle += sweep;
        return (
          <path
            key={slice.label}
            className="donut-segment"
            d={path}
            pathLength="100"
            style={{ animationDelay: `${380 + idx * 180}ms` }}
            fill="none"
            stroke={STAGE_COLORS[idx % STAGE_COLORS.length]}
            strokeWidth="24"
            strokeLinecap="butt"
          />
        );
      })}
      <text x="110" y="102" textAnchor="middle" className="donut-total">{total}</text>
      <text x="110" y="123" textAnchor="middle" className="donut-sub">Total</text>
    </svg>
  );
};

const GasFamilyMiniChart = ({ values, total }) => {
  const safeTotal = Math.max(1, total);
  let angle = 0;

  return (
    <svg viewBox="0 0 120 120" className="gas-mini-chart" role="img" aria-label="Gas sensor status distribution">
      <circle cx="60" cy="60" r="36" fill="none" stroke="#e9f1f8" strokeWidth="14" />
      {GAS_FAMILY_BUCKETS.map((bucket, bucketIdx) => {
        const count = toNumber(values[bucket.key], 0);
        if (!count) return null;

        const animationDelayMs = 760 + bucketIdx * 160;

        const sweep = (count / safeTotal) * 360;
        if (sweep >= 359.999) {
          return (
            <circle
              key={bucket.key}
              className="gas-mini-segment"
              cx="60"
              cy="60"
              r="36"
              pathLength="100"
              style={{ animationDelay: `${animationDelayMs}ms` }}
              fill="none"
              stroke={bucket.color}
              strokeWidth="14"
            />
          );
        }

        const path = describeArc(60, 60, 36, angle, angle + sweep);
        angle += sweep;
        return (
          <path
            key={bucket.key}
            className="gas-mini-segment"
            d={path}
            pathLength="100"
            style={{ animationDelay: `${animationDelayMs}ms` }}
            fill="none"
            stroke={bucket.color}
            strokeWidth="14"
            strokeLinecap="butt"
          />
        );
      })}
      <text x="60" y="56" textAnchor="middle" className="gas-mini-total">{total}</text>
      <text x="60" y="71" textAnchor="middle" className="gas-mini-sub">Total</text>
    </svg>
  );
};

const FaultGroupedBarChart = ({ rows }) => {
  const chartRows = (Array.isArray(rows) ? rows : []).filter((row) => toNumber(row?.total, 0) > 0);
  if (chartRows.length === 0) return null;

  const series = [
    { key: 'low', label: 'Low', color: '#70b654' },
    { key: 'total', label: 'Total', color: '#6ea9df' },
    { key: 'medium', label: 'Medium', color: '#f0c541' },
    { key: 'high', label: 'High', color: '#e74c45' },
  ];

  const maxValue = Math.max(
    1,
    ...chartRows.flatMap((row) =>
      series.map((item) => {
        const value = Number(row?.[item.key]);
        return Number.isFinite(value) ? value : 0;
      }),
    ),
  );

  const height = 245;
  const m = { top: 14, right: 12, bottom: 54, left: 36 };
  const groupW = 112;
  const intrinsicPlotW = groupW * chartRows.length;
  const plotW = Math.max(520, intrinsicPlotW);
  const width = m.left + m.right + plotW;
  const chartStartX = m.left + Math.max(0, (plotW - intrinsicPlotW) / 2);
  const plotH = height - m.top - m.bottom;
  const barStep = groupW / (series.length + 1);
  const barW = Math.max(8, Math.min(18, barStep * 0.72));
  const tickCount = maxValue <= 4 ? Math.max(1, maxValue) : 4;
  const tickStep = maxValue <= 4 ? 1 : Math.ceil(maxValue / 4);
  const yMax = tickStep * tickCount;

  const trimLabel = (text) => {
    const value = String(text || '');
    return value.length > 11 ? `${value.slice(0, 11)}...` : value;
  };

  return (
    <div className="fault-grouped-chart" aria-label="Chamber faults grouped bar chart">
      <div className="fault-grouped-chart-scroll">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="fault-grouped-chart-svg"
          role="img"
          aria-label="Fault counts by chamber and priority"
          style={{ width: `${width}px`, height: `${height}px` }}
        >
          {Array.from({ length: tickCount + 1 }).map((_, idx) => {
            const ratio = idx / tickCount;
            const y = m.top + ratio * plotH;
            const tickValue = yMax - idx * tickStep;
            return (
              <g key={`tick-${idx}`}>
                <line x1={m.left} y1={y} x2={m.left + plotW} y2={y} className="fault-grid-line" />
                <text x={m.left - 6} y={y + 4} className="fault-axis-label" textAnchor="end">{tickValue}</text>
              </g>
            );
          })}

          {chartRows.map((row, gIdx) => {
            const baseX = chartStartX + gIdx * groupW;
            const clusterW = barStep * series.length;
            const clusterX = baseX + (groupW - clusterW) / 2;

            return (
              <g key={`grp-${row.chamberId || row.chamberName || gIdx}`}>
                {series.map((item, sIdx) => {
                  const raw = Number(row?.[item.key]);
                  const value = Number.isFinite(raw) ? raw : 0;
                  const barH = (value / yMax) * plotH;
                  const x = clusterX + sIdx * barStep + (barStep - barW) / 2;
                  const y = m.top + plotH - barH;
                  const animationDelayMs = 620 + gIdx * 110 + sIdx * 70;
                  return (
                    <rect
                      key={`${item.key}-${gIdx}`}
                      x={x}
                      y={y}
                      width={barW}
                      height={barH}
                      rx="2"
                      fill={item.color}
                      className="fault-bar"
                      style={{ animationDelay: `${animationDelayMs}ms` }}
                    />
                  );
                })}

                <text
                  x={baseX + groupW / 2}
                  y={m.top + plotH + 18}
                  textAnchor="middle"
                  className="fault-x-label"
                  title={row.chamberName}
                >
                  {trimLabel(row.chamberName)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <div className="fault-chart-legend-row" role="presentation">
        {series.map((item) => (
          <span className="fault-chart-legend-item" key={`legend-${item.key}`}>
            <span className="fault-chart-legend-dot" style={{ backgroundColor: item.color }} />
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
};


const LoadingDashboardSkeleton = () => (
  <div className="dashboard-loader-wrap" aria-label="Loading dashboard">
    <Spin size="large" />
  </div>
);

const applyTiltFromPointer = (event) => {
  const card = event.currentTarget;
  const rect = card.getBoundingClientRect();
  const px = (event.clientX - rect.left) / rect.width;
  const py = (event.clientY - rect.top) / rect.height;

  const rotateY = (px - 0.5) * 7.5;
  const rotateX = (0.5 - py) * 7.5;

  card.style.setProperty('--tilt-rx', `${rotateX.toFixed(2)}deg`);
  card.style.setProperty('--tilt-ry', `${rotateY.toFixed(2)}deg`);
  card.style.setProperty('--glare-x', `${(px * 100).toFixed(1)}%`);
  card.style.setProperty('--glare-y', `${(py * 100).toFixed(1)}%`);
};

const resetTilt = (event) => {
  const card = event.currentTarget;
  card.style.setProperty('--tilt-rx', '0deg');
  card.style.setProperty('--tilt-ry', '0deg');
  card.style.setProperty('--glare-x', '50%');
  card.style.setProperty('--glare-y', '50%');
};

const toNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const firstDefined = (obj, paths, fallback = undefined) => {
  for (const path of paths) {
    const value = get(obj, path);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return fallback;
};

const parseDateOrNow = (value) => {
  const parsed = value ? new Date(value) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date();
};

const parseNullableDate = (value) => {
  if (value === null || value === undefined || value === '') return null;

  const numeric = Number(value);
  const date = Number.isFinite(numeric) ? new Date(numeric) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const extractStageCounts = (snapshot) => {
  const raw =
    firstDefined(snapshot, ['stageBreakdown', 'chamberStateMix', 'stateMix', 'charts.stageBreakdown'], null) || [];

  if (!Array.isArray(raw) || raw.length === 0) {
    return null;
  }

  const labelMap = {
    overdue: 'overdue',
    'due < 30 days': 'due30',
    'due<30d': 'due30',
    'due within 30 days': 'due30',
    stable: 'upToDate',
    'up to date': 'upToDate',
    inactive: 'inactive',
    'inactive chambers': 'inactive',
    'status unknown': 'statusUnknown',
    'unknown status': 'statusUnknown',
  };

  return raw.reduce(
    (acc, item) => {
      const rawLabel = String(firstDefined(item, ['label', 'name', 'type'], '')).toLowerCase();
      const normalizedLabel = labelMap[rawLabel];
      const count = toNumber(firstDefined(item, ['count', 'value', 'total'], 0), 0);

      if (normalizedLabel) {
        acc[normalizedLabel] += count;
      }

      return acc;
    },
    { overdue: 0, due30: 0, upToDate: 0, inactive: 0, statusUnknown: 0 },
  );
};

const getSnapshotSites = (snapshot) => {
  const sites = firstDefined(snapshot, ['sites', 'scope.sites'], []);
  return Array.isArray(sites) ? sites : [];
};

const toScopeSiteOptions = (sites) =>
  (Array.isArray(sites) ? sites : [])
    .map((site) => {
      const id = toSiteIdentifier(firstDefined(site, ['id', 'siteId', 'site.id', 'site_id'], null));
      const name = String(firstDefined(site, ['name', 'siteName', 'site.name', 'site_name'], '')).trim();

      return {
        id,
        name,
      };
    })
    .filter((site) => site.id !== null || site.name);

const getSnapshotChambers = (snapshot) =>
  getSnapshotSites(snapshot).flatMap((site) => (Array.isArray(site?.chambers) ? site.chambers : []));

const toFaultDisplayItem = (fault, fallbackLabel) => {
  if (fault && typeof fault === 'object' && !Array.isArray(fault)) {
    const question = firstDefined(fault, ['question', 'title', 'name', 'faultName', 'description', 'message', 'code'], '');
    return {
      question: String(question || '').trim(),
      selectedAnswer: String(firstDefined(fault, ['selectedAnswer', 'answer', 'value', 'status'], '')),
      note: String(firstDefined(fault, ['note', 'notes', 'comment', 'remarks'], '')),
    };
  }

  if (typeof fault === 'string') {
    const trimmed = fault.trim();
    if (!trimmed) return { question: '', selectedAnswer: '', note: '' };

    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') {
        return toFaultDisplayItem(parsed, fallbackLabel);
      }
    } catch (err) {
      // Keep plain text values as readable question strings.
    }

    return {
      question: trimmed,
      selectedAnswer: '',
      note: '',
    };
  }

  if (typeof fault === 'number') {
    return {
      question: '',
      selectedAnswer: '',
      note: '',
    };
  }

  return { question: '', selectedAnswer: '', note: '' };
};

const normalizeFaultCollection = (value, fallbackLabel) => {
  if (Array.isArray(value)) {
    return value
      .map((item, idx) => toFaultDisplayItem(item, `${fallbackLabel} ${idx + 1}`))
      .filter((item) => Boolean(item?.question));
  }
  if (value && typeof value === 'object') {
    const nested =
      firstDefined(value, ['items', 'list', 'faults', 'questions', 'results', 'values'], null) || [];
    if (Array.isArray(nested)) {
      return nested
        .map((item, idx) => toFaultDisplayItem(item, `${fallbackLabel} ${idx + 1}`))
        .filter((item) => Boolean(item?.question));
    }
  }
  if (typeof value === 'string' && value.trim()) {
    return [toFaultDisplayItem(value.trim(), fallbackLabel)];
  }
  return [];
};

const buildPriorityFaultItems = (source, priorityLabel, count) => {
  const keyPrefix = priorityLabel.toLowerCase();
  const collection =
    firstDefined(source, [
      `${keyPrefix}PriorityFaultItems`,
      `${keyPrefix}PriorityFaultList`,
      `${keyPrefix}PriorityFaultsList`,
      `${keyPrefix}PriorityFaultsDetails`,
      `${keyPrefix}Priorities`,
      `${keyPrefix}PriorityQuestions`,
      `${keyPrefix}Faults`,
      `faults.${keyPrefix}`,
      `faults.${keyPrefix}Items`,
      `faults.${keyPrefix}List`,
      `priorities.${keyPrefix}`,
      `priority.${keyPrefix}`,
      `${keyPrefix}Priority`,
    ], []) || [];

  const normalized = normalizeFaultCollection(collection, `${priorityLabel} Priority Fault`);
  if (normalized.length > 0) return normalized;

  return [];
};

const deriveFaultRowsFromChambers = (chambers) =>
  chambers
    .map((row, idx) => {
      const high = toNumber(firstDefined(row, ['highPriorityFaults', 'high', 'faults.high'], 0), 0);
      const medium = toNumber(firstDefined(row, ['mediumPriorityFaults', 'medium', 'faults.medium'], 0), 0);
      const low = toNumber(firstDefined(row, ['lowPriorityFaults', 'low', 'faults.low'], 0), 0);
      const totalFaults = toNumber(firstDefined(row, ['totalFaults', 'faultCount', 'total'], high + medium + low), 0);

      if (totalFaults <= 0) return null;

      const highFaultItems = buildPriorityFaultItems(row, 'High', high);
      const mediumFaultItems = buildPriorityFaultItems(row, 'Medium', medium);
      const lowFaultItems = buildPriorityFaultItems(row, 'Low', low);

      return {
        chamberId: firstDefined(row, ['chamberId', 'id'], `chamber-${idx}`),
        chamberName: firstDefined(row, ['chamberName', 'name', 'chamberDisplayId'], `Chamber ${idx + 1}`),
        siteId: firstDefined(row, ['siteId', 'site.id', 'site_id', 'siteIdentifier'], null),
        siteName: firstDefined(row, ['siteName', 'site.name'], 'Assigned Site'),
        total: totalFaults,
        high,
        medium,
        low,
        status: high > 0 ? 'high' : medium > 0 ? 'attention' : 'healthy',
        highFaultItems,
        mediumFaultItems,
        lowFaultItems,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      if (b.high !== a.high) return b.high - a.high;
      if (b.medium !== a.medium) return b.medium - a.medium;
      return String(a.chamberName).localeCompare(String(b.chamberName));
    });

const deriveExpiryCountersFromChambers = (chambers, now) => {
  const counters = {
    oxygen: { expired: 0, due60: 0 },
    chemicals: { expired: 0, due60: 0 },
    gas: { expired: 0, due60: 0 },
    cartridges: { expired: 0, due60: 0 },
  };

  const classifyExpiry = (dateValue, bucket) => {
    if (!dateValue) return;
    const days = differenceInCalendarDays(parseDateOrNow(dateValue), now);
    if (days < 0) counters[bucket].expired += 1;
    else if (days <= 60) counters[bucket].due60 += 1;
  };

  chambers.forEach((chamber) => {
    classifyExpiry(firstDefined(chamber, ['oxygenCandleExpiry', 'oxygen.expiry']), 'oxygen');
    classifyExpiry(firstDefined(chamber, ['chemicalExpiry', 'chemicals.expiry']), 'chemicals');
    classifyExpiry(firstDefined(chamber, ['gasSensorExpiry', 'gas.expiry']), 'gas');
    classifyExpiry(firstDefined(chamber, ['cartridgeExpiry', 'cartridge.expiry']), 'cartridges');
  });

  return counters;
};

const deriveGasSensorSummaryFromChambers = (chambers, now) => {
  return chambers.reduce(
    (acc, chamber) => {
      const expiry = firstDefined(chamber, ['gasSensorExpiry', 'gas.expiry'], null);
      if (!expiry) return acc;

      const days = differenceInCalendarDays(parseDateOrNow(expiry), now);
      if (days < 0) acc.overdue += 1;
      else if (days <= 30) acc.due30 += 1;
      return acc;
    },
    { overdue: 0, due30: 0 },
  );
};

const classifyGasSensorBucket = (expiryValue, now) => {
  if (!expiryValue) return { bucket: 'noRecord', daysToExpiry: null };
  const daysToExpiry = differenceInCalendarDays(parseDateOrNow(expiryValue), now);
  if (daysToExpiry < 0) return { bucket: 'overdue', daysToExpiry };
  if (daysToExpiry <= 30) return { bucket: 'due30', daysToExpiry };
  return { bucket: 'upToDate', daysToExpiry };
};

const classifyExpiry30dBucket = (expiryValue, now) => {
  if (!expiryValue) return { bucket: 'noRecord', daysToExpiry: null };
  const daysToExpiry = differenceInCalendarDays(parseDateOrNow(expiryValue), now);
  if (daysToExpiry < 0) return { bucket: 'overdue', daysToExpiry };
  if (daysToExpiry <= 30) return { bucket: 'due30', daysToExpiry };
  return { bucket: 'upToDate', daysToExpiry };
};

const toGasSensorRow = (chamber, bucketResult, familyType) => ({
  id: chamber.id,
  name: chamber.name,
  siteName: chamber.siteName,
  tenantName: chamber.tenantName,
  imageUrl: chamber.imageUrl,
  bucket: bucketResult.bucket,
  daysToExpiry: bucketResult.daysToExpiry,
  expiryDate: familyType === 'externalGasNode' ? chamber.externalGasNodeSensorExpiry : chamber.auraFxGasSensorExpiry,
  noSensorRecord:
    familyType === 'externalGasNode'
      ? !chamber.externalGasNodeSensorExpiry
      : !chamber.auraFxGasSensorExpiry,
});

const sortGasSensorRows = (rows) =>
  [...rows].sort((a, b) => {
    const aDays = a.daysToExpiry;
    const bDays = b.daysToExpiry;
    if (aDays === null && bDays === null) return String(a.name).localeCompare(String(b.name));
    if (aDays === null) return 1;
    if (bDays === null) return -1;
    if (aDays !== bDays) return aDays - bDays;
    return String(a.name).localeCompare(String(b.name));
  });

const toConsumableRow = (chamber, daysToExpiry = null) => ({
  id: chamber.id,
  name: chamber.name,
  siteName: chamber.siteName,
  tenantName: chamber.tenantName,
  imageUrl: chamber.imageUrl,
  daysToExpiry,
});

const deriveSingleConsumableFamilyFromChambers = (chambers, now, { applicablePath, expiryPath }) => {
  const activeChambers = chambers.filter((chamber) => !chamber.isInactive);
  const groups = {
    overdue: [],
    due30: [],
    upToDate: [],
    noRecord: [],
  };

  activeChambers.forEach((chamber) => {
    if (!chamber?.[applicablePath]) return;

    const bucketResult = classifyExpiry30dBucket(chamber?.[expiryPath], now);
    groups[bucketResult.bucket].push(toConsumableRow(chamber, bucketResult.daysToExpiry));
  });

  return {
    applicableCount: groups.overdue.length + groups.due30.length + groups.upToDate.length + groups.noRecord.length,
    groups: {
      overdue: sortGasSensorRows(groups.overdue),
      due30: sortGasSensorRows(groups.due30),
      upToDate: sortGasSensorRows(groups.upToDate),
      noRecord: sortGasSensorRows(groups.noRecord),
    },
  };
};

const deriveGasTubesFamilyFromChambers = (chambers, now) => {
  const activeChambers = chambers.filter((chamber) => !chamber.isInactive);
  const groups = {
    overdue: [],
    due30: [],
    upToDate: [],
    noRecord: [],
  };

  activeChambers.forEach((chamber) => {
    const tubeEntries = [
      { applicable: chamber.o2TubeApplicable, expiry: chamber.o2TubeExpiry },
      { applicable: chamber.coTubeApplicable, expiry: chamber.coTubeExpiry },
      { applicable: chamber.co2TubeApplicable, expiry: chamber.co2TubeExpiry },
    ].filter((entry) => entry.applicable);

    if (tubeEntries.length === 0) return;

    const statuses = tubeEntries.map((entry) => classifyExpiry30dBucket(entry.expiry, now));
    const hasOverdue = statuses.some((status) => status.bucket === 'overdue');
    const hasDue30 = statuses.some((status) => status.bucket === 'due30');
    const hasRecords = statuses.some((status) => status.bucket !== 'noRecord');
    const nonNullDays = statuses
      .map((status) => status.daysToExpiry)
      .filter((days) => days !== null && days !== undefined);
    const earliestDays = nonNullDays.length > 0 ? Math.min(...nonNullDays) : null;

    const chamberBucket = hasOverdue
      ? 'overdue'
      : hasDue30
      ? 'due30'
      : hasRecords
      ? 'upToDate'
      : 'noRecord';
    groups[chamberBucket].push(toConsumableRow(chamber, earliestDays));
  });

  return {
    applicableCount: groups.overdue.length + groups.due30.length + groups.upToDate.length + groups.noRecord.length,
    groups: {
      overdue: sortGasSensorRows(groups.overdue),
      due30: sortGasSensorRows(groups.due30),
      upToDate: sortGasSensorRows(groups.upToDate),
      noRecord: sortGasSensorRows(groups.noRecord),
    },
  };
};

const deriveConsumableFamiliesFromChambers = (chambers, now) => ({
  oxygenCandles: deriveSingleConsumableFamilyFromChambers(chambers, now, {
    applicablePath: 'oxygenCandleApplicable',
    expiryPath: 'oxygenCandleExpiry',
  }),
  co2Cartridge: deriveSingleConsumableFamilyFromChambers(chambers, now, {
    applicablePath: 'co2CartridgeApplicable',
    expiryPath: 'co2CartridgeExpiry',
  }),
  coCartridge: deriveSingleConsumableFamilyFromChambers(chambers, now, {
    applicablePath: 'coCartridgeApplicable',
    expiryPath: 'coCartridgeExpiry',
  }),
  gasTubes: deriveGasTubesFamilyFromChambers(chambers, now),
});

const deriveGasSensorFamiliesFromChambers = (chambers, now) => {
  const activeChambers = chambers.filter((chamber) => !chamber.isInactive);

  const auraGroups = {
    overdue: [],
    due30: [],
    upToDate: [],
    noRecord: [],
  };

  activeChambers.forEach((chamber) => {
    const bucketResult = classifyGasSensorBucket(chamber.auraFxGasSensorExpiry, now);
    auraGroups[bucketResult.bucket].push(toGasSensorRow(chamber, bucketResult, 'auraFx'));
  });

  const externalScope = activeChambers.filter((chamber) => chamber.externalGasNodeApplicable);
  const externalGroups = {
    overdue: [],
    due30: [],
    upToDate: [],
    noRecord: [],
  };

  externalScope.forEach((chamber) => {
    const bucketResult = classifyGasSensorBucket(chamber.externalGasNodeSensorExpiry, now);
    externalGroups[bucketResult.bucket].push(toGasSensorRow(chamber, bucketResult, 'externalGasNode'));
  });

  return {
    auraFx: {
      applicableCount: activeChambers.length,
      groups: {
        overdue: sortGasSensorRows(auraGroups.overdue),
        due30: sortGasSensorRows(auraGroups.due30),
        upToDate: sortGasSensorRows(auraGroups.upToDate),
        noRecord: sortGasSensorRows(auraGroups.noRecord),
      },
    },
    externalGasNode: {
      applicableCount: externalScope.length,
      groups: {
        overdue: sortGasSensorRows(externalGroups.overdue),
        due30: sortGasSensorRows(externalGroups.due30),
        upToDate: sortGasSensorRows(externalGroups.upToDate),
        noRecord: sortGasSensorRows(externalGroups.noRecord),
      },
    },
  };
};

const deriveUrgencyRowsFromChambers = (chambers, now) =>
  chambers
    .map((row, idx) => {
      const nextServiceDate = parseDateOrNow(firstDefined(row, ['nextServiceDate', 'service.nextServiceDate'], new Date()));
      const soonestExpiryDate = parseDateOrNow(firstDefined(row, ['soonestExpiryDate', 'expiry.date', 'gasSensorExpiry', 'oxygenCandleExpiry', 'chemicalExpiry', 'cartridgeExpiry'], new Date()));
      const serviceDaysLeft = toNumber(firstDefined(row, ['serviceDaysLeft'], differenceInCalendarDays(nextServiceDate, now)), 0);
      const totalFaults = toNumber(firstDefined(row, ['totalFaults', 'faultCount', 'total'], 0), 0);

      return {
        id: firstDefined(row, ['chamberId', 'id'], `urg-${idx}`),
        name: firstDefined(row, ['chamberName', 'name', 'chamberDisplayId'], `Chamber ${idx + 1}`),
        tenantName: firstDefined(row, ['tenantName', 'tenant.name'], ''),
        siteName: firstDefined(row, ['siteName', 'site.name'], 'Assigned Site'),
        serviceDaysLeft,
        soonestExpiryType: firstDefined(row, ['soonestExpiryType', 'expiry.type'], 'Expiry'),
        soonestExpiryDate,
        nextServiceDate,
        totalFaults,
      };
    })
    .sort((a, b) => {
      if (b.totalFaults !== a.totalFaults) return b.totalFaults - a.totalFaults;
      return a.serviceDaysLeft - b.serviceDaysLeft;
    })
    .slice(0, 8);

const getChamberImageUrl = (chamber) => {
  return '/product_images/MineSAFE_Standard_Design';
};

const isChamberInactive = (chamber) => {
  const explicitInactive = firstDefined(chamber, ['inactive', 'isInactive'], null);
  if (explicitInactive !== null && explicitInactive !== undefined) return Boolean(explicitInactive);

  const explicitActive = firstDefined(chamber, ['active', 'isActive', 'enabled'], null);
  if (explicitActive !== null && explicitActive !== undefined) return !explicitActive;

  const status = String(firstDefined(chamber, ['status', 'state', 'chamberStatus', 'serviceFormStatus'], '')).toLowerCase();
  return ['inactive', 'disabled', 'deactivated', 'not_available', 'not available'].includes(status);
};

const isServiceStatusUnknown = (chamber) => {
  const explicitUnknown = firstDefined(chamber, ['serviceStatusUnknown', 'statusUnknown'], null);
  if (explicitUnknown !== null) {
    return explicitUnknown === true || String(explicitUnknown).toLowerCase() === 'true';
  }

  const serviceStatus = String(firstDefined(chamber, ['serviceStatus', 'nextServiceStatus'], '')).toLowerCase();
  if (['unknown', 'unavailable', 'not recorded'].includes(serviceStatus)) return true;

  const serviceDaysLeft = firstDefined(chamber, ['serviceDaysLeft'], null);
  const serviceDate = firstDefined(chamber, ['nextServiceDate', 'service.nextServiceDate'], null);
  return serviceDaysLeft === null && parseNullableDate(serviceDate) === null;
};

const toChamberCatalog = (chambers, now) =>
  chambers.map((chamber, idx) => {
    const serviceDaysLeftValue = firstDefined(chamber, ['serviceDaysLeft'], null);
    const serviceDateValue = firstDefined(chamber, ['nextServiceDate', 'service.nextServiceDate'], null);
    const parsedServiceDate = parseNullableDate(serviceDateValue);
    const nextServiceDate = parsedServiceDate || new Date();
    const serviceDaysLeft = toNumber(
      serviceDaysLeftValue,
      differenceInCalendarDays(nextServiceDate, now),
    );

    return {
      id: firstDefined(chamber, ['chamberId', 'id'], `chamber-${idx}`),
      name: firstDefined(chamber, ['chamberName', 'name', 'chamberDisplayId'], `Chamber ${idx + 1}`),
      siteId: firstDefined(chamber, ['siteId', 'site.id', 'site_id', 'siteIdentifier'], null),
      siteName: firstDefined(chamber, ['siteName', 'site.name'], 'Assigned Site'),
      tenantName: firstDefined(chamber, ['tenantName', 'tenant.name'], ''),
      nextServiceDate,
      serviceDaysLeft,
      serviceStatusUnknown: isServiceStatusUnknown(chamber),
      serviceOverdue: Boolean(chamber?.serviceOverdue || serviceDaysLeft < 0),
      isInactive: isChamberInactive(chamber),
      auraFxGasSensorExpiry: firstDefined(chamber, ['auraFxGasSensorExpiry', 'gasSensors.auraFx.expiry', 'auraFx.expiry'], null),
      externalGasNodeSensorExpiry: firstDefined(chamber, ['externalGasNodeSensorExpiry', 'gasSensors.externalGasNode.expiry', 'externalGasNode.expiry'], null),
      externalGasNodeApplicable: (() => {
        const explicitApplicable = firstDefined(chamber, ['externalGasNodeApplicable'], null);
        if (explicitApplicable !== null && explicitApplicable !== undefined) return Boolean(explicitApplicable);
        return Boolean(firstDefined(chamber, ['externalGasNodeSensorExpiry', 'gasSensors.externalGasNode.expiry', 'externalGasNode.expiry'], null));
      })(),
      oxygenCandleApplicable: (() => {
        const explicitApplicable = firstDefined(chamber, ['oxygenCandleApplicable'], null);
        if (explicitApplicable !== null && explicitApplicable !== undefined) return Boolean(explicitApplicable);
        return firstDefined(chamber, ['oxygenCandleExpiry'], null) !== null;
      })(),
      oxygenCandleExpiry: firstDefined(chamber, ['oxygenCandleExpiry'], null),
      co2CartridgeApplicable: Boolean(firstDefined(chamber, ['co2CartridgeApplicable'], false)),
      co2CartridgeExpiry: firstDefined(chamber, ['co2CartridgeExpiry'], null),
      coCartridgeApplicable: Boolean(firstDefined(chamber, ['coCartridgeApplicable'], false)),
      coCartridgeExpiry: firstDefined(chamber, ['coCartridgeExpiry'], null),
      o2TubeApplicable: Boolean(firstDefined(chamber, ['o2TubeApplicable'], false)),
      o2TubeExpiry: firstDefined(chamber, ['o2TubeExpiry'], null),
      coTubeApplicable: Boolean(firstDefined(chamber, ['coTubeApplicable'], false)),
      coTubeExpiry: firstDefined(chamber, ['coTubeExpiry'], null),
      co2TubeApplicable: Boolean(firstDefined(chamber, ['co2TubeApplicable'], false)),
      co2TubeExpiry: firstDefined(chamber, ['co2TubeExpiry'], null),
      imageUrl: getChamberImageUrl(chamber),
    };
  });

const normalizeStageBreakdown = (snapshot, total, dueNow, dueInMonth, upToDate, inactiveChambers) => {
  const raw =
    firstDefined(snapshot, ['stageBreakdown', 'chamberStateMix', 'stateMix', 'charts.stageBreakdown'], null) || [];

  if (Array.isArray(raw) && raw.length > 0) {
    const labelMap = {
      overdue: 'Overdue',
      'due < 30 days': 'Due < 30 Days',
      'due<30d': 'Due < 30 Days',
      'due within 30 days': 'Due < 30 Days',
      stable: 'Up to Date',
      'up to date': 'Up to Date',
      inactive: 'Inactive Chambers',
      'inactive chambers': 'Inactive Chambers',
    };

    const merged = raw.reduce(
      (acc, item) => {
        const rawLabel = String(firstDefined(item, ['label', 'name', 'type'], '')).toLowerCase();
        const normalizedLabel = labelMap[rawLabel] || firstDefined(item, ['label', 'name', 'type'], '');
        const count = toNumber(firstDefined(item, ['count', 'value', 'total'], 0), 0);
        if (normalizedLabel === 'Overdue') acc.overdue += count;
        else if (normalizedLabel === 'Due < 30 Days') acc.due30 += count;
        else if (normalizedLabel === 'Up to Date') acc.upToDate += count;
        else if (normalizedLabel === 'Inactive Chambers') acc.inactive += count;
        return acc;
      },
      { overdue: 0, due30: 0, upToDate: 0, inactive: 0 },
    );

    return [
      { label: 'Overdue', count: merged.overdue },
      { label: 'Due < 30 Days', count: merged.due30 },
      { label: 'Up to Date', count: merged.upToDate },
      { label: 'Decommissioned', count: merged.inactive || inactiveChambers || 0 },
    ];
  }

  return [
    { label: 'Overdue', count: dueNow },
    { label: 'Due < 30 Days', count: dueInMonth },
    { label: 'Up to Date', count: upToDate || Math.max(0, total - dueNow - dueInMonth) },
    { label: 'Decommissioned', count: inactiveChambers || 0 },
  ];
};

const emptyStats = () => ({
  total: 0,
  inactiveChambers: 0,
  statusUnknown: 0,
  dueNow: 0,
  dueInMonth: 0,
  upToDate: 0,
  stageBreakdown: [
    { label: 'Overdue', count: 0 },
    { label: 'Due < 30 Days', count: 0 },
    { label: 'Up to Date', count: 0 },
    { label: 'Decommissioned', count: 0 },
  ],
  chamberFaultRows: [],
  expiryCounters: {
    oxygen: { expired: 0, due60: 0 },
    chemicals: { expired: 0, due60: 0 },
    gas: { expired: 0, due60: 0 },
    cartridges: { expired: 0, due60: 0 },
  },
  gasSensorCards: {
    externalGasNode: { applicableCount: 0, overdue: 0, due30: 0, stable: 0, noRecord: 0, groups: { overdue: [], due30: [], upToDate: [], noRecord: [] } },
    auraFx: { applicableCount: 0, overdue: 0, due30: 0, stable: 0, noRecord: 0, groups: { overdue: [], due30: [], upToDate: [], noRecord: [] } },
  },
  consumableCards: {
    oxygenCandles: { applicableCount: 0, overdue: 0, due30: 0, upToDate: 0, noRecord: 0, groups: { overdue: [], due30: [], upToDate: [], noRecord: [] } },
    co2Cartridge: { applicableCount: 0, overdue: 0, due30: 0, upToDate: 0, noRecord: 0, groups: { overdue: [], due30: [], upToDate: [], noRecord: [] } },
    gasTubes: { applicableCount: 0, overdue: 0, due30: 0, upToDate: 0, noRecord: 0, groups: { overdue: [], due30: [], upToDate: [], noRecord: [] } },
    coCartridge: { applicableCount: 0, overdue: 0, due30: 0, upToDate: 0, noRecord: 0, groups: { overdue: [], due30: [], upToDate: [], noRecord: [] } },
  },
  gasSensorSummary: { overdue: 0, due30: 0 },
  urgencyRows: [],
  siteNames: [],
  chambers: [],
});

const buildSiteScopedStats = (baseStats, scopedSites) => {
  const chambers = Array.isArray(baseStats?.chambers) ? baseStats.chambers : [];
  const faultRows = Array.isArray(baseStats?.chamberFaultRows) ? baseStats.chamberFaultRows : [];
  const sites = Array.isArray(scopedSites) ? scopedSites : [];

  if (sites.length === 0) return baseStats;

  const allowedSiteIds = new Set(
    sites
      .map((site) => normalizeSiteIdentifier(site?.id))
      .filter(Boolean),
  );
  const allowedSiteNames = new Set(
    sites
      .map((site) => normalizeSiteLabel(site?.name))
      .filter(Boolean),
  );

  const visibleChambers = chambers.filter((chamber) => {
    const chamberSiteId = normalizeSiteIdentifier(firstDefined(chamber, ['siteId', 'site.id', 'site_id', 'siteIdentifier'], null));
    const chamberSiteName = normalizeSiteLabel(firstDefined(chamber, ['siteName', 'site.name'], ''));

    if (allowedSiteIds.size > 0 && chamberSiteId) return allowedSiteIds.has(chamberSiteId);
    if (allowedSiteNames.size > 0 && chamberSiteName) return allowedSiteNames.has(chamberSiteName);

    return false;
  });

  const visibleChamberIds = new Set(
    visibleChambers
      .map((chamber) => normalizeSiteIdentifier(firstDefined(chamber, ['id', 'chamberId'], null)))
      .filter(Boolean),
  );
  const visibleChamberNames = new Set(
    visibleChambers
      .map((chamber) => String(firstDefined(chamber, ['name', 'chamberName'], '')).trim().toLowerCase())
      .filter(Boolean),
  );

  const visibleFaultRows = faultRows.filter((row) => {
    const rowId = normalizeSiteIdentifier(firstDefined(row, ['chamberId', 'id'], null));
    const rowName = String(firstDefined(row, ['chamberName', 'name'], '')).trim().toLowerCase();

    if (visibleChamberIds.size > 0 && rowId) return visibleChamberIds.has(rowId);
    if (visibleChamberNames.size > 0 && rowName) return visibleChamberNames.has(rowName);
    return false;
  });

  const now = new Date();
  const inactiveChambers = visibleChambers.filter((item) => item.isInactive).length;
  const statusUnknown = visibleChambers.filter((item) => item.serviceStatusUnknown).length;
  const dueNow = visibleChambers.filter((item) => item.serviceOverdue || item.serviceDaysLeft < 0).length;
  const dueInMonth = visibleChambers.filter(
    (item) => !item.serviceStatusUnknown && item.serviceDaysLeft >= 0 && item.serviceDaysLeft <= 30,
  ).length;
  const upToDate = visibleChambers.filter((item) => !item.serviceStatusUnknown && item.serviceDaysLeft > 30).length;

  const gasSensorFamilies = deriveGasSensorFamiliesFromChambers(visibleChambers, now);
  const consumableFamilies = deriveConsumableFamiliesFromChambers(visibleChambers, now);

  return {
    ...baseStats,
    total: visibleChambers.length,
    inactiveChambers,
    statusUnknown,
    dueNow,
    dueInMonth,
    upToDate,
    stageBreakdown: normalizeStageBreakdown({}, visibleChambers.length, dueNow, dueInMonth, upToDate, inactiveChambers),
    chamberFaultRows: visibleFaultRows.length > 0 ? visibleFaultRows : deriveFaultRowsFromChambers(visibleChambers),
    expiryCounters: deriveExpiryCountersFromChambers(visibleChambers, now),
    gasSensorSummary: deriveGasSensorSummaryFromChambers(visibleChambers, now),
    gasSensorCards: {
      externalGasNode: {
        applicableCount: gasSensorFamilies.externalGasNode.applicableCount,
        overdue: gasSensorFamilies.externalGasNode.groups.overdue.length,
        due30: gasSensorFamilies.externalGasNode.groups.due30.length,
        stable: gasSensorFamilies.externalGasNode.groups.upToDate.length,
        noRecord: gasSensorFamilies.externalGasNode.groups.noRecord.length,
        groups: gasSensorFamilies.externalGasNode.groups,
      },
      auraFx: {
        applicableCount: gasSensorFamilies.auraFx.applicableCount,
        overdue: gasSensorFamilies.auraFx.groups.overdue.length,
        due30: gasSensorFamilies.auraFx.groups.due30.length,
        stable: gasSensorFamilies.auraFx.groups.upToDate.length,
        noRecord: gasSensorFamilies.auraFx.groups.noRecord.length,
        groups: gasSensorFamilies.auraFx.groups,
      },
    },
    consumableCards: {
      oxygenCandles: {
        applicableCount: consumableFamilies.oxygenCandles.applicableCount,
        overdue: consumableFamilies.oxygenCandles.groups.overdue.length,
        due30: consumableFamilies.oxygenCandles.groups.due30.length,
        upToDate: consumableFamilies.oxygenCandles.groups.upToDate.length,
        noRecord: consumableFamilies.oxygenCandles.groups.noRecord.length,
        groups: consumableFamilies.oxygenCandles.groups,
      },
      co2Cartridge: {
        applicableCount: consumableFamilies.co2Cartridge.applicableCount,
        overdue: consumableFamilies.co2Cartridge.groups.overdue.length,
        due30: consumableFamilies.co2Cartridge.groups.due30.length,
        upToDate: consumableFamilies.co2Cartridge.groups.upToDate.length,
        noRecord: consumableFamilies.co2Cartridge.groups.noRecord.length,
        groups: consumableFamilies.co2Cartridge.groups,
      },
      gasTubes: {
        applicableCount: consumableFamilies.gasTubes.applicableCount,
        overdue: consumableFamilies.gasTubes.groups.overdue.length,
        due30: consumableFamilies.gasTubes.groups.due30.length,
        upToDate: consumableFamilies.gasTubes.groups.upToDate.length,
        noRecord: consumableFamilies.gasTubes.groups.noRecord.length,
        groups: consumableFamilies.gasTubes.groups,
      },
      coCartridge: {
        applicableCount: consumableFamilies.coCartridge.applicableCount,
        overdue: consumableFamilies.coCartridge.groups.overdue.length,
        due30: consumableFamilies.coCartridge.groups.due30.length,
        upToDate: consumableFamilies.coCartridge.groups.upToDate.length,
        noRecord: consumableFamilies.coCartridge.groups.noRecord.length,
        groups: consumableFamilies.coCartridge.groups,
      },
    },
    chambers: visibleChambers,
  };
};

const mapSnapshotToStats = (rawPayload) => {
  const snapshot = firstDefined(rawPayload, ['snapshot', 'data', 'payload'], rawPayload) || {};
  const now = new Date();
  const stageCounts = extractStageCounts(snapshot);
  const chambers = getSnapshotChambers(snapshot);
  const summary = firstDefined(snapshot, ['summary', 'kpis', 'metrics'], {}) || {};

  const total = toNumber(
    firstDefined(
      snapshot,
      ['total', 'kpis.totalChambers', 'metrics.totalChambers', 'summary.totalChambers', 'summary.chamberCount'],
      stageCounts
        ? stageCounts.overdue + stageCounts.due30 + stageCounts.upToDate + stageCounts.statusUnknown
        : 0,
    ),
    0,
  );
  const unknownServiceStatusCount = chambers.filter(isServiceStatusUnknown).length;
  const dueNow = toNumber(
    firstDefined(
      snapshot,
      [
        'dueNow',
        'kpis.serviceOverdue',
        'metrics.serviceOverdue',
        'summary.serviceOverdue',
        'summary.serviceOverdueChambers',
      ],
      stageCounts?.overdue ?? chambers.filter((chamber) =>
        !isServiceStatusUnknown(chamber) && (chamber?.serviceOverdue || toNumber(chamber?.serviceDaysLeft, 0) < 0),
      ).length,
    ),
    0,
  );
  const dueInMonth = toNumber(
    firstDefined(
      snapshot,
      [
        'dueInMonth',
        'kpis.dueWithin30Days',
        'metrics.dueWithin30Days',
        'summary.dueWithin30Days',
        'summary.dueWithin30DaysChambers',
      ],
      stageCounts?.due30 ?? chambers.filter((chamber) => {
        if (isServiceStatusUnknown(chamber)) return false;
        const days = toNumber(chamber?.serviceDaysLeft, differenceInCalendarDays(parseDateOrNow(chamber?.nextServiceDate), now));
        return days >= 0 && days <= 30;
      }).length,
    ),
    0,
  );
  const upToDate = toNumber(
    firstDefined(
      snapshot,
      ['upToDate', 'kpis.upToDate', 'metrics.upToDate', 'summary.upToDate', 'summary.upToDateChambers'],
      stageCounts?.upToDate ?? Math.max(0, total - dueNow - dueInMonth - unknownServiceStatusCount),
    ),
    0,
  );

  const chamberCatalog = toChamberCatalog(chambers, now);
  const inactiveChambers = toNumber(
    firstDefined(snapshot, ['inactiveChambers', 'summary.inactiveChambers', 'kpis.inactiveChambers', 'metrics.inactiveChambers'], chamberCatalog.filter((item) => item.isInactive).length),
    0,
  );
  const statusUnknown = toNumber(
    firstDefined(
      snapshot,
      ['statusUnknown', 'unknownStatusCount', 'summary.statusUnknown', 'summary.unknownStatus', 'kpis.statusUnknown', 'metrics.statusUnknown'],
      stageCounts?.statusUnknown ?? chamberCatalog.filter((item) => item.serviceStatusUnknown).length,
    ),
    0,
  );

  const stageBreakdown = normalizeStageBreakdown(snapshot, total, dueNow, dueInMonth, upToDate, inactiveChambers);

  const faultRowsRaw = firstDefined(snapshot, ['chamberFaultRows', 'chamberFaults', 'lists.chamberFaults'], []);
  const chamberFaultRows = ((Array.isArray(faultRowsRaw) ? faultRowsRaw : []).length > 0
    ? (Array.isArray(faultRowsRaw) ? faultRowsRaw : [])
    .map((row, idx) => {
      const high = toNumber(firstDefined(row, ['high', 'highPriority', 'faults.high'], 0), 0);
      const medium = toNumber(firstDefined(row, ['medium', 'mediumPriority', 'faults.medium'], 0), 0);
      const low = toNumber(firstDefined(row, ['low', 'lowPriority', 'faults.low'], 0), 0);
      const totalFaults = toNumber(firstDefined(row, ['total', 'faultCount', 'totalFaults'], high + medium + low), 0);
      const status = high > 0 ? 'high' : totalFaults > 0 ? 'attention' : 'healthy';
      const highFaultItems = buildPriorityFaultItems(row, 'High', high);
      const mediumFaultItems = buildPriorityFaultItems(row, 'Medium', medium);
      const lowFaultItems = buildPriorityFaultItems(row, 'Low', low);

      return {
        chamberId: firstDefined(row, ['chamberId', 'id'], `chamber-${idx}`),
        chamberName: firstDefined(row, ['chamberName', 'name', 'chamber.name'], `Chamber ${idx + 1}`),
        siteId: firstDefined(row, ['siteId', 'site.id', 'site_id', 'siteIdentifier'], null),
        siteName: firstDefined(row, ['siteName', 'site.name'], 'Assigned Site'),
        total: totalFaults,
        high,
        medium,
        low,
        status,
        highFaultItems,
        mediumFaultItems,
        lowFaultItems,
      };
    })
    .sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      if (b.high !== a.high) return b.high - a.high;
      if (b.medium !== a.medium) return b.medium - a.medium;
      return String(a.chamberName).localeCompare(String(b.chamberName));
    })
    : deriveFaultRowsFromChambers(chambers));

  const mapExpiry = (rootKey, fallbackKeys = []) => ({
    expired: toNumber(firstDefined(snapshot, [`expiryCounters.${rootKey}.expired`, `expiry.${rootKey}.expired`, ...fallbackKeys.map((k) => `${k}.expired`)], 0), 0),
    due60: toNumber(firstDefined(snapshot, [`expiryCounters.${rootKey}.due60`, `expiry.${rootKey}.due60`, ...fallbackKeys.map((k) => `${k}.due60`)], 0), 0),
  });

  const derivedExpiryCounters = deriveExpiryCountersFromChambers(chambers, now);
  const expiryCounters = {
    oxygen: {
      expired: toNumber(firstDefined(snapshot, ['expiryCounters.oxygen.expired', 'expiry.oxygen.expired', 'summary.oxygenCandlesExpired'], derivedExpiryCounters.oxygen.expired), 0),
      due60: toNumber(firstDefined(snapshot, ['expiryCounters.oxygen.due60', 'expiry.oxygen.due60', 'summary.oxygenCandlesDue60Days'], derivedExpiryCounters.oxygen.due60), 0),
    },
    chemicals: {
      expired: toNumber(firstDefined(snapshot, ['expiryCounters.chemicals.expired', 'expiry.chemicals.expired', 'summary.chemicalsExpired'], derivedExpiryCounters.chemicals.expired), 0),
      due60: toNumber(firstDefined(snapshot, ['expiryCounters.chemicals.due60', 'expiry.chemicals.due60', 'summary.chemicalsDue60Days'], derivedExpiryCounters.chemicals.due60), 0),
    },
    gas: {
      expired: toNumber(firstDefined(snapshot, ['expiryCounters.gas.expired', 'expiry.gas.expired', 'summary.gasSensorsExpired'], derivedExpiryCounters.gas.expired), 0),
      due60: toNumber(firstDefined(snapshot, ['expiryCounters.gas.due60', 'expiry.gas.due60', 'summary.gasSensorsDue60Days'], derivedExpiryCounters.gas.due60), 0),
    },
    cartridges: {
      expired: toNumber(firstDefined(snapshot, ['expiryCounters.cartridges.expired', 'expiry.cartridges.expired', 'summary.cartridgesExpired'], derivedExpiryCounters.cartridges.expired), 0),
      due60: toNumber(firstDefined(snapshot, ['expiryCounters.cartridges.due60', 'expiry.cartridges.due60', 'summary.cartridgesDue60Days'], derivedExpiryCounters.cartridges.due60), 0),
    },
  };

  const derivedGasSensorSummary = deriveGasSensorSummaryFromChambers(chambers, now);
  const gasSensorSummary = {
    overdue: toNumber(firstDefined(snapshot, ['gasSensorSummary.overdue', 'gasSensors.summary.overdue'], derivedGasSensorSummary.overdue), 0),
    due30: toNumber(firstDefined(snapshot, ['gasSensorSummary.due30', 'gasSensors.summary.due30'], derivedGasSensorSummary.due30), 0),
  };

  const urgencyRaw = firstDefined(snapshot, ['urgencyRows', 'priorityChambers', 'lists.priorityChambers'], []);
  const urgencyRows = ((Array.isArray(urgencyRaw) ? urgencyRaw : []).length > 0 ? (Array.isArray(urgencyRaw) ? urgencyRaw : []).map((row, idx) => {
    const nextServiceDate = parseDateOrNow(firstDefined(row, ['nextServiceDate', 'service.nextServiceDate'], new Date()));
    const soonestExpiryDate = parseDateOrNow(firstDefined(row, ['soonestExpiryDate', 'expiry.date'], new Date()));
    const serviceDaysLeft = toNumber(firstDefined(row, ['serviceDaysLeft'], differenceInCalendarDays(nextServiceDate, now)), 0);

    return {
      id: firstDefined(row, ['id', 'chamberId'], `urg-${idx}`),
      name: firstDefined(row, ['name', 'chamberName'], `Chamber ${idx + 1}`),
      tenantName: firstDefined(row, ['tenantName', 'tenant.name'], ''),
      siteName: firstDefined(row, ['siteName', 'site.name'], 'Assigned Site'),
      serviceDaysLeft,
      soonestExpiryType: firstDefined(row, ['soonestExpiryType', 'expiry.type'], 'Expiry'),
      soonestExpiryDate,
      nextServiceDate,
    };
  }) : deriveUrgencyRowsFromChambers(chambers, now));

  const siteNamesRaw = firstDefined(snapshot, ['scope.siteNames', 'siteNames'], []);
  const siteNames = Array.isArray(siteNamesRaw)
    ? siteNamesRaw
        .map((item) => (typeof item === 'string' ? item : item?.name))
        .filter(Boolean)
    : getSnapshotSites(snapshot).map((site) => firstDefined(site, ['siteName', 'name'], '')).filter(Boolean);

  const derivedGasSensorFamilies = deriveGasSensorFamiliesFromChambers(chamberCatalog, now);
  const derivedConsumableFamilies = deriveConsumableFamiliesFromChambers(chamberCatalog, now);

  const normalizeGasFamilyCounts = ({ applicableCount, overdue, due30, upToDateRaw, noRecord, fallbackUpToDate }) => {
    let upToDate = toNumber(upToDateRaw, fallbackUpToDate);
    const safeApplicableCount = Math.max(0, toNumber(applicableCount, 0));
    const safeOverdue = Math.max(0, toNumber(overdue, 0));
    const safeDue30 = Math.max(0, toNumber(due30, 0));
    const safeNoRecord = Math.max(0, toNumber(noRecord, 0));

    const rawTotal = safeOverdue + safeDue30 + upToDate + safeNoRecord;

    // If upstream up-to-date still includes no-record entries, remove overlap.
    if (rawTotal > safeApplicableCount && upToDate >= safeNoRecord) {
      upToDate -= safeNoRecord;
    }

    const maxUpToDate = Math.max(0, safeApplicableCount - safeOverdue - safeDue30 - safeNoRecord);
    upToDate = Math.max(0, Math.min(upToDate, maxUpToDate));

    return {
      applicableCount: safeApplicableCount,
      overdue: safeOverdue,
      due30: safeDue30,
      stable: upToDate,
      noRecord: safeNoRecord,
    };
  };

  const externalCounts = normalizeGasFamilyCounts({
    applicableCount: toNumber(
      firstDefined(snapshot, ['summary.externalGasNodeApplicableChambers', 'externalGasNodeApplicableChambers'], derivedGasSensorFamilies.externalGasNode.applicableCount),
      derivedGasSensorFamilies.externalGasNode.applicableCount,
    ),
    overdue: toNumber(
      firstDefined(snapshot, ['summary.externalGasNodeOverdueChambers', 'externalGasNodeOverdueChambers'], derivedGasSensorFamilies.externalGasNode.groups.overdue.length),
      derivedGasSensorFamilies.externalGasNode.groups.overdue.length,
    ),
    due30: toNumber(
      firstDefined(snapshot, ['summary.externalGasNodeDue30DaysChambers', 'externalGasNodeDue30DaysChambers'], derivedGasSensorFamilies.externalGasNode.groups.due30.length),
      derivedGasSensorFamilies.externalGasNode.groups.due30.length,
    ),
    upToDateRaw: firstDefined(snapshot, ['summary.externalGasNodeUpToDateChambers', 'externalGasNodeUpToDateChambers'], derivedGasSensorFamilies.externalGasNode.groups.upToDate.length),
    noRecord: toNumber(
      firstDefined(snapshot, ['summary.externalGasNodeNoSensorRecordsChambers', 'summary.externalGasNodeNoSensorRecords', 'externalGasNodeNoSensorRecordsChambers'], derivedGasSensorFamilies.externalGasNode.groups.noRecord.length),
      derivedGasSensorFamilies.externalGasNode.groups.noRecord.length,
    ),
    fallbackUpToDate: derivedGasSensorFamilies.externalGasNode.groups.upToDate.length,
  });

  const auraCounts = normalizeGasFamilyCounts({
    applicableCount: derivedGasSensorFamilies.auraFx.applicableCount,
    overdue: toNumber(
      firstDefined(snapshot, ['summary.auraFxGasSensorsOverdueChambers', 'auraFxGasSensorsOverdueChambers'], derivedGasSensorFamilies.auraFx.groups.overdue.length),
      derivedGasSensorFamilies.auraFx.groups.overdue.length,
    ),
    due30: toNumber(
      firstDefined(snapshot, ['summary.auraFxGasSensorsDue30DaysChambers', 'auraFxGasSensorsDue30DaysChambers'], derivedGasSensorFamilies.auraFx.groups.due30.length),
      derivedGasSensorFamilies.auraFx.groups.due30.length,
    ),
    upToDateRaw: firstDefined(snapshot, ['summary.auraFxGasSensorsUpToDateChambers', 'auraFxGasSensorsUpToDateChambers'], derivedGasSensorFamilies.auraFx.groups.upToDate.length),
    noRecord: toNumber(
      firstDefined(snapshot, ['summary.auraFxGasSensorsNoSensorRecordsChambers', 'summary.auraFxGasSensorsNoSensorRecords', 'auraFxGasSensorsNoSensorRecordsChambers'], derivedGasSensorFamilies.auraFx.groups.noRecord.length),
      derivedGasSensorFamilies.auraFx.groups.noRecord.length,
    ),
    fallbackUpToDate: derivedGasSensorFamilies.auraFx.groups.upToDate.length,
  });

  const gasSensorCardsResolved = {
    externalGasNode: {
      applicableCount: externalCounts.applicableCount,
      overdue: externalCounts.overdue,
      due30: externalCounts.due30,
      stable: externalCounts.stable,
      noRecord: externalCounts.noRecord,
      groups: derivedGasSensorFamilies.externalGasNode.groups,
    },
    auraFx: {
      applicableCount: auraCounts.applicableCount,
      overdue: auraCounts.overdue,
      due30: auraCounts.due30,
      stable: auraCounts.stable,
      noRecord: auraCounts.noRecord,
      groups: derivedGasSensorFamilies.auraFx.groups,
    },
  };

  const resolveConsumableCard = ({
    derived,
    applicablePath,
    overduePath,
    due30Path,
    upToDatePath,
    noRecordPath,
  }) => {
    const applicableCount = toNumber(firstDefined(snapshot, applicablePath, derived.applicableCount), derived.applicableCount);
    const overdue = toNumber(firstDefined(snapshot, overduePath, derived.groups.overdue.length), derived.groups.overdue.length);
    const due30 = toNumber(firstDefined(snapshot, due30Path, derived.groups.due30.length), derived.groups.due30.length);
    const noRecord = toNumber(
      firstDefined(snapshot, noRecordPath, derived.groups.noRecord.length),
      derived.groups.noRecord.length,
    );
    const availableUpToDate = Math.max(0, applicableCount - overdue - due30 - noRecord);
    const upToDate = Math.min(
      availableUpToDate,
      toNumber(firstDefined(snapshot, upToDatePath, derived.groups.upToDate.length), derived.groups.upToDate.length),
    );

    return {
      applicableCount,
      overdue,
      due30,
      upToDate,
      noRecord,
      groups: derived.groups,
    };
  };

  const consumableCardsResolved = {
    oxygenCandles: resolveConsumableCard({
      derived: derivedConsumableFamilies.oxygenCandles,
      applicablePath: ['summary.oxygenCandleApplicableChambers', 'oxygenCandleApplicableChambers'],
      overduePath: ['summary.oxygenCandleOverdueChambers', 'oxygenCandleOverdueChambers'],
      due30Path: ['summary.oxygenCandleDue30DaysChambers', 'oxygenCandleDue30DaysChambers'],
      upToDatePath: ['summary.oxygenCandleUpToDateChambers', 'oxygenCandleUpToDateChambers'],
      noRecordPath: ['summary.oxygenCandleNoRecordsChambers', 'summary.oxygenCandleNoExpiryDateChambers'],
    }),
    co2Cartridge: resolveConsumableCard({
      derived: derivedConsumableFamilies.co2Cartridge,
      applicablePath: ['summary.co2CartridgeApplicableChambers', 'co2CartridgeApplicableChambers'],
      overduePath: ['summary.co2CartridgeOverdueChambers', 'co2CartridgeOverdueChambers'],
      due30Path: ['summary.co2CartridgeDue30DaysChambers', 'co2CartridgeDue30DaysChambers'],
      upToDatePath: ['summary.co2CartridgeUpToDateChambers', 'co2CartridgeUpToDateChambers'],
      noRecordPath: ['summary.co2CartridgeNoRecordsChambers', 'summary.co2CartridgeNoExpiryDateChambers'],
    }),
    coCartridge: resolveConsumableCard({
      derived: derivedConsumableFamilies.coCartridge,
      applicablePath: ['summary.coCartridgeApplicableChambers', 'coCartridgeApplicableChambers'],
      overduePath: ['summary.coCartridgeOverdueChambers', 'coCartridgeOverdueChambers'],
      due30Path: ['summary.coCartridgeDue30DaysChambers', 'coCartridgeDue30DaysChambers'],
      upToDatePath: ['summary.coCartridgeUpToDateChambers', 'coCartridgeUpToDateChambers'],
      noRecordPath: ['summary.coCartridgeNoRecordsChambers', 'summary.coCartridgeNoExpiryDateChambers'],
    }),
    gasTubes: {
      applicableCount: derivedConsumableFamilies.gasTubes.applicableCount,
      overdue: derivedConsumableFamilies.gasTubes.groups.overdue.length,
      due30: derivedConsumableFamilies.gasTubes.groups.due30.length,
      upToDate: derivedConsumableFamilies.gasTubes.groups.upToDate.length,
      noRecord: derivedConsumableFamilies.gasTubes.groups.noRecord.length,
      groups: derivedConsumableFamilies.gasTubes.groups,
    },
  };

  return {
    total,
    inactiveChambers,
    statusUnknown,
    dueNow,
    dueInMonth,
    upToDate,
    stageBreakdown,
    chamberFaultRows,
    expiryCounters,
    gasSensorCards: gasSensorCardsResolved,
    consumableCards: consumableCardsResolved,
    gasSensorSummary,
    urgencyRows,
    siteNames,
    chambers: chamberCatalog,
  };
};

const SupportDashboard = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector(getUser);
  const siteList = useSelector(getSiteList);
  const gasSensorExpiryData = useSelector(getGasSensorExpiryData);
  const gasSensorExpiryLoading = useSelector(getGasSensorExpiryLoading);
  const gasSensorExpiryError = useSelector(getGasSensorExpiryError);

  const isSuperAdminRole = useMemo(() => hasRole(user, ROLE.SA), [user]);
  const isMinearcRole = useMemo(() => hasRole(user, ROLE.MINEARC), [user]);
  const isTenantRole = useMemo(() => hasRole(user, ROLE.TA), [user]);
  const isSiteRole = useMemo(() => hasRole(user, ROLE.SU), [user]);

  const canSwitchTenant = isSuperAdminRole || isMinearcRole;
  const canFilterBySite = canSwitchTenant || isTenantRole;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tenantOptions, setTenantOptions] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [siteOptions, setSiteOptions] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [stats, setStats] = useState(emptyStats());
  const [liveStatus, setLiveStatus] = useState('connecting');
  const [activeKpiModal, setActiveKpiModal] = useState('');
  const [activeGasSensorModal, setActiveGasSensorModal] = useState('');
  const [activeGasSensorChamber, setActiveGasSensorChamber] = useState(null);
  const [activeConsumableModal, setActiveConsumableModal] = useState('');
  const [activeConsumableChamber, setActiveConsumableChamber] = useState(null);
  const [faultModalSelection, setFaultModalSelection] = useState(null);
  const [activeFaultChamber, setActiveFaultChamber] = useState(null);
  const [faultModalLoading, setFaultModalLoading] = useState(false);
  const [faultModalError, setFaultModalError] = useState('');
  const [faultModalApiResponse, setFaultModalApiResponse] = useState(null);
  const [faultSummaryById, setFaultSummaryById] = useState({});
  const [faultSummaryLoadingById, setFaultSummaryLoadingById] = useState({});
  const [lastSnapshotPayload, setLastSnapshotPayload] = useState(null);
  const [lastStreamPayload, setLastStreamPayload] = useState(null);
  const streamRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const faultRequestIdRef = useRef(0);
  const faultSummaryRequestsRef = useRef(new Set());
  const faultTableRef = useRef(null);

  const userTenantId = useMemo(() => getTenantIdentifierFromUser(user), [user]);
  const userTenantName = useMemo(() => getTenantNameFromUser(user), [user]);
  const siteRoleScopedSites = useMemo(
    () =>
      toScopeSiteOptions(siteList),
    [siteList],
  );

  useEffect(() => {
    if (!isSiteRole) return;
    dispatch(getPaginatedSiteList({ page: 0, isAscending: false, sortColumn: 'id' }));
  }, [dispatch, isSiteRole]);

  useEffect(() => {
    if (!canFilterBySite) return;
    setSelectedSite('');
    setSiteOptions([]);
  }, [canFilterBySite, selectedTenant]);

  useEffect(() => {
    let cancelled = false;

    const loadTenantsForGlobalRoles = async () => {
      try {
        const payload = await GET('v1/admin/tenant/minimal');
        const options = (Array.isArray(payload) ? payload : [])
          .map((item) => ({ value: item.identifier, label: item.name || item.identifier }))
          .filter((item) => item.value)
          .sort((a, b) => String(a.label).localeCompare(String(b.label)));

        return options;
      } catch (err) {
        return [];
      }
    };

    const bootstrapScope = async () => {
      if (canSwitchTenant) {
        const options = await loadTenantsForGlobalRoles();
        if (cancelled) return;
        setTenantOptions(options);
        if (!selectedTenant && options.length > 0) {
          setSelectedTenant(options[0].value);
        }
        return;
      }

      if (isTenantRole || isSiteRole) {
        const tenantId = userTenantId;
        const label = userTenantName || userTenantId || 'My Tenant';
        const options = tenantId ? [{ value: tenantId, label }] : [];
        if (!cancelled) {
          setTenantOptions(options);
          if (tenantId && !selectedTenant) setSelectedTenant(tenantId);
        }
      }
    };

    bootstrapScope();

    return () => {
      cancelled = true;
    };
  }, [canSwitchTenant, isSiteRole, isTenantRole, selectedTenant, userTenantId, userTenantName]);

  useEffect(() => {
    if (canSwitchTenant && !selectedTenant) return;

    let cancelled = false;
    const tenantIdForScope = selectedTenant || userTenantId || '';
    const token = getSseAccessToken();

    const snapshotParams = new URLSearchParams();
    if (tenantIdForScope) snapshotParams.append('tenantId', tenantIdForScope);

    const streamParams = new URLSearchParams(snapshotParams.toString());
    if (token) streamParams.append('access_token', token);

    const snapshotQuery = snapshotParams.toString();
    const streamQuery = streamParams.toString();
    const snapshotPath = `v1/report/dashboard/snapshot${snapshotQuery ? `?${snapshotQuery}` : ''}`;
    const streamPath = `${API_BASE_URL || ''}/v1/report/dashboard/stream${streamQuery ? `?${streamQuery}` : ''}`;

    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const closeStream = () => {
      if (streamRef.current) {
        streamRef.current.close();
        streamRef.current = null;
      }
    };

    const handleSnapshotPayload = (payload, source = 'snapshot') => {
      const mapped = mapSnapshotToStats(payload);
      const snapshotSites = toScopeSiteOptions(getSnapshotSites(payload));
      if (!cancelled) {
        if (canFilterBySite) {
          setSiteOptions(snapshotSites);
          setSelectedSite((current) => {
            if (!snapshotSites.length) return '';
            if (current === '') return '';
            const currentSite = snapshotSites.find((site) => String(site.id || site.name) === String(current || ''));
            if (currentSite) return current;
            if (current === null || current === undefined) {
              if (isTenantRole) return '';
              return String(snapshotSites[0].id || snapshotSites[0].name || '');
            }
            return '';
          });
        }
        if (source === 'stream') setLastStreamPayload(payload);
        else setLastSnapshotPayload(payload);
        setStats(mapped);
        setError('');
      }
    };

    const hydrateSnapshot = async () => {
      setLoading(true);
      setError('');
      try {
        const payload = await GET(snapshotPath);
        if (!cancelled) handleSnapshotPayload(payload, 'snapshot');
      } catch (err) {
        if (!cancelled) {
          setStats(emptyStats());
          setLastSnapshotPayload({ error: err });
          setError('Failed to load dashboard snapshot. Waiting for live updates...');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const onStreamPayload = (event) => {
      try {
        const parsed = JSON.parse(event.data || '{}');
        // Receiving a payload proves the stream is live even if onopen lags behind a proxy flush.
        if (!cancelled) {
          reconnectAttemptRef.current = 0;
          setLiveStatus('live');
        }
        handleSnapshotPayload(parsed, 'stream');
      } catch (err) {
        // Ignore malformed stream payloads and keep listening.
      }
    };

    function scheduleReconnect() {
      if (cancelled) return;
      clearReconnectTimer();

      reconnectAttemptRef.current += 1;
      const attempt = reconnectAttemptRef.current;
      const delayMs = Math.min(30000, 1000 * 2 ** Math.min(attempt, 5));

      setLiveStatus('reconnecting');
      reconnectTimerRef.current = setTimeout(() => {
        if (!cancelled) openStream();
      }, delayMs);
    }

    function openStream() {
      closeStream();
      clearReconnectTimer();
      setLiveStatus('connecting');

      try {
        const source = new EventSource(streamPath);
        streamRef.current = source;

        source.onopen = () => {
          if (cancelled) return;
          reconnectAttemptRef.current = 0;
          setLiveStatus('live');
        };

        source.addEventListener('dashboard-snapshot', onStreamPayload);
        source.onmessage = onStreamPayload;

        source.onerror = () => {
          if (cancelled) return;
          closeStream();
          scheduleReconnect();
        };
      } catch (err) {
        scheduleReconnect();
      }
    }

    hydrateSnapshot();
    openStream();

    return () => {
      cancelled = true;
      clearReconnectTimer();
      closeStream();
    };
  }, [canFilterBySite, isTenantRole, selectedTenant, userTenantId]);

  const selectedTenantLabel = useMemo(() => {
    return tenantOptions.find((opt) => opt.value === selectedTenant)?.label || userTenantName || 'Tenant';
  }, [selectedTenant, tenantOptions, userTenantName]);

  const selectedSiteOption = useMemo(() => {
    const options = isSiteRole ? siteRoleScopedSites : siteOptions;
    return options.find((site) => String(site.id || site.name) === String(selectedSite || '')) || null;
  }, [isSiteRole, selectedSite, siteOptions, siteRoleScopedSites]);

  const selectedSiteLabel = useMemo(() => {
    if (isSiteRole) {
      if (siteRoleScopedSites[0]?.name) return siteRoleScopedSites[0].name;
      return 'Site';
    }

    if (selectedSiteOption?.name) return selectedSiteOption.name;

    return 'Site';
  }, [isSiteRole, selectedSiteOption, siteRoleScopedSites]);

  const scopeSites = useMemo(() => {
    if (isSiteRole) return siteRoleScopedSites;
    if (canFilterBySite && selectedSiteOption) return [selectedSiteOption];
    return [];
  }, [canFilterBySite, isSiteRole, selectedSiteOption, siteRoleScopedSites]);

  const dashboardStats = useMemo(
    () => (scopeSites.length > 0 ? buildSiteScopedStats(stats, scopeSites) : stats),
    [scopeSites, stats],
  );

  const hasDashboardData = useMemo(
    () => Boolean(lastStreamPayload) || Boolean(lastSnapshotPayload && !lastSnapshotPayload.error),
    [lastSnapshotPayload, lastStreamPayload],
  );

  const hasExplicitFaultDetailShape = (payload) => {
    const fields = [
      'high',
      'highPriorityCount',
      'highPriorityFaultCount',
      'highPriorityFaults',
      'medium',
      'mediumPriorityCount',
      'mediumPriorityFaultCount',
      'mediumPriorityFaults',
      'low',
      'lowPriorityCount',
      'lowPriorityFaultCount',
      'lowPriorityFaults',
      'summary.high',
      'summary.medium',
      'summary.low',
      'counts.high',
      'counts.medium',
      'counts.low',
      'faults.high',
      'faults.medium',
      'faults.low',
      'highPriorityFaultItems',
      'mediumPriorityFaultItems',
      'lowPriorityFaultItems',
      'highPriorityFaultList',
      'mediumPriorityFaultList',
      'lowPriorityFaultList',
      'highPriorities',
      'mediumPriorities',
      'lowPriorities',
    ];

    return fields.some((path) => firstDefined(payload, [path], undefined) !== undefined);
  };

  const normalizeFaultDetailsResponse = (rawPayload, row) => {
    const payload = firstDefined(rawPayload, ['payload', 'data', 'faultDetails', 'result'], rawPayload) || {};
    const parsedHigh = toNumber(
      firstDefined(payload, ['high', 'highPriorityCount', 'summary.high', 'counts.high'], row?.high || 0),
      row?.high || 0,
    );
    const parsedMedium = toNumber(
      firstDefined(payload, ['medium', 'mediumPriorityCount', 'summary.medium', 'counts.medium'], row?.medium || 0),
      row?.medium || 0,
    );
    const parsedLow = toNumber(
      firstDefined(payload, ['low', 'lowPriorityCount', 'summary.low', 'counts.low'], row?.low || 0),
      row?.low || 0,
    );

    const high = toNumber(
      firstDefined(
        payload,
        [
          'high',
          'highPriorityCount',
          'highPriorityFaultCount',
          'highPriorityFaults',
          'summary.high',
          'summary.highPriority',
          'summary.highPriorityCount',
          'summary.highPriorityFaultCount',
          'summary.highPriorityFaults',
          'counts.high',
          'counts.highPriority',
          'totals.high',
        ],
        parsedHigh,
      ),
      parsedHigh,
    );
    const medium = toNumber(
      firstDefined(
        payload,
        [
          'medium',
          'mediumPriorityCount',
          'mediumPriorityFaultCount',
          'mediumPriorityFaults',
          'summary.medium',
          'summary.mediumPriority',
          'summary.mediumPriorityCount',
          'summary.mediumPriorityFaultCount',
          'summary.mediumPriorityFaults',
          'counts.medium',
          'counts.mediumPriority',
          'totals.medium',
        ],
        parsedMedium,
      ),
      parsedMedium,
    );
    const low = toNumber(
      firstDefined(
        payload,
        [
          'low',
          'lowPriorityCount',
          'lowPriorityFaultCount',
          'lowPriorityFaults',
          'summary.low',
          'summary.lowPriority',
          'summary.lowPriorityCount',
          'summary.lowPriorityFaultCount',
          'summary.lowPriorityFaults',
          'counts.low',
          'counts.lowPriority',
          'totals.low',
        ],
        parsedLow,
      ),
      parsedLow,
    );

    const highItems = buildPriorityFaultItems(payload, 'High', high);
    const mediumItems = buildPriorityFaultItems(payload, 'Medium', medium);
    const lowItems = buildPriorityFaultItems(payload, 'Low', low);

    const explicitFaultShape = hasExplicitFaultDetailShape(payload);
    const apiReturnedAnyItems = highItems.length > 0 || mediumItems.length > 0 || lowItems.length > 0;
    const apiReturnedAnyCounts = high > 0 || medium > 0 || low > 0;

    const shouldFallbackToRowCounts = !explicitFaultShape && !apiReturnedAnyItems && !apiReturnedAnyCounts;

    return {
      ...row,
      high: shouldFallbackToRowCounts ? row?.high || 0 : high,
      medium: shouldFallbackToRowCounts ? row?.medium || 0 : medium,
      low: shouldFallbackToRowCounts ? row?.low || 0 : low,
      highFaultItems: shouldFallbackToRowCounts ? row?.highFaultItems || [] : highItems,
      mediumFaultItems: shouldFallbackToRowCounts ? row?.mediumFaultItems || [] : mediumItems,
      lowFaultItems: shouldFallbackToRowCounts ? row?.lowFaultItems || [] : lowItems,
      apiReturnedExplicitZeros: explicitFaultShape && high === 0 && medium === 0 && low === 0 && !apiReturnedAnyItems,
    };
  };

  const openFaultDetails = async (row) => {
    setFaultModalSelection(row);
    setActiveFaultChamber(null);
    setFaultModalError('');
    setFaultModalApiResponse(null);
    setFaultModalLoading(true);

    const requestId = faultRequestIdRef.current + 1;
    faultRequestIdRef.current = requestId;

    const chamberId = firstDefined(row, ['chamberId', 'id'], null);
    const tenantIdForScope = selectedTenant || userTenantId || '';

    if (!chamberId) {
      setFaultModalLoading(false);
      setFaultModalError('No chamber ID was found for this row.');
      return;
    }

    try {
      const endpoint = CHAMBER_FAULTS_ENDPOINT(chamberId);
      const params = {
        ...(tenantIdForScope ? { tenantId: tenantIdForScope } : {}),
      };

      const payload = await GET(CHAMBER_FAULTS_ENDPOINT(chamberId), {
        params,
      });

      if (faultRequestIdRef.current !== requestId) return;

      setFaultModalApiResponse({
        request: {
          endpoint,
          params,
        },
        response: payload,
      });

      const normalized = normalizeFaultDetailsResponse(payload, row);
      setActiveFaultChamber(normalized);
    } catch (err) {
      if (faultRequestIdRef.current !== requestId) return;

      setFaultModalApiResponse({
        request: {
          endpoint: CHAMBER_FAULTS_ENDPOINT(chamberId),
          params: {
            ...(tenantIdForScope ? { tenantId: tenantIdForScope } : {}),
          },
        },
        error: {
          status: err?.status,
          message: err?.message,
          data: err?.data,
        },
      });

      setFaultModalError(
        err?.message ||
          'Could not load detailed chamber faults from the server. Showing derived fallback details.',
      );
    } finally {
      if (faultRequestIdRef.current === requestId) {
        setFaultModalLoading(false);
      }
    }
  };

  const goToFaultChamberReports = () => {
    const chamberId = firstDefined(activeFaultChamber, ['chamberId', 'id'], null);
    if (!chamberId) return;

    history.push({
      pathname: `/chambers/${chamberId}/reports`,
      state: {
        chamberName: firstDefined(activeFaultChamber, ['chamberName', 'name'], ''),
      },
    });
  };

  const goToGasChamberReports = () => {
    const chamberId = firstDefined(activeGasSensorChamber, ['chamberId', 'id'], null);
    if (!chamberId) return;

    history.push({
      pathname: `/chambers/${chamberId}/reports`,
      state: {
        chamberName: firstDefined(activeGasSensorChamber, ['name', 'chamberName'], ''),
      },
    });
  };

  const goToConsumableChamberReports = () => {
    const chamberId = firstDefined(activeConsumableChamber, ['chamberId', 'id'], null);
    if (!chamberId) return;

    history.push({
      pathname: `/chambers/${chamberId}/reports`,
      state: {
        chamberName: firstDefined(activeConsumableChamber, ['name', 'chamberName'], ''),
      },
    });
  };

  const chamberListByCard = useMemo(() => {
    const allChambers = Array.isArray(dashboardStats.chambers) ? dashboardStats.chambers : [];
    const active = allChambers.filter((item) => !item.isInactive);
    const inactive = allChambers.filter((item) => item.isInactive);
    const overdue = active.filter((item) => item.serviceOverdue || item.serviceDaysLeft < 0);
    const due30 = active.filter(
      (item) => !item.serviceStatusUnknown && item.serviceDaysLeft >= 0 && item.serviceDaysLeft <= 30,
    );
    const upToDate = active.filter((item) => !item.serviceStatusUnknown && item.serviceDaysLeft > 30);
    const unknown = active.filter((item) => item.serviceStatusUnknown);
    const chamberIdentity = (item) => {
      const id = firstDefined(item, ['id', 'chamberId'], null);
      return id !== null && id !== undefined ? `id:${String(id).toLowerCase()}` : `name:${String(firstDefined(item, ['name', 'chamberName'], '')).toLowerCase()}`;
    };
    const activeIdentities = new Set(active.map(chamberIdentity));
    const faultRows = Array.isArray(dashboardStats.chamberFaultRows) ? dashboardStats.chamberFaultRows : [];
    const criticalFaultRows = faultRows.filter(
      (item) => toNumber(item.high, 0) > 0 && (active.length === 0 || activeIdentities.has(chamberIdentity(item))),
    );
    const criticalIdentities = new Set(criticalFaultRows.map(chamberIdentity));
    const critical = active.length > 0
      ? active.filter((item) => criticalIdentities.has(chamberIdentity(item)))
      : criticalFaultRows;
    const attention = active.length > 0
      ? active.filter((item) =>
        item.serviceOverdue || item.serviceDaysLeft < 0 || criticalIdentities.has(chamberIdentity(item)),
      )
      : criticalFaultRows;

    return {
      total: allChambers,
      active,
      inactive,
      overdue,
      due30,
      upToDate,
      critical,
      attention,
      unknown,
    };
  }, [dashboardStats.chamberFaultRows, dashboardStats.chambers]);

  const activeSiteCount = new Set(
    chamberListByCard.active
      .map((item) => normalizeSiteLabel(item.siteName))
      .filter((name) => name && name !== 'assigned site' && name !== 'unknown site'),
  ).size || dashboardStats.siteNames.length;
  const hasChamberCatalog = chamberListByCard.total.length > 0;
  const activeChamberCount = Math.max(0, dashboardStats.total - dashboardStats.inactiveChambers);
  const criticalChamberCount = chamberListByCard.critical.length;
  const overdueChamberCount = hasChamberCatalog ? chamberListByCard.overdue.length : dashboardStats.dueNow;
  const needsAttentionCount = hasChamberCatalog
    ? chamberListByCard.attention.length
    : Math.max(criticalChamberCount, dashboardStats.dueNow);

  const modalMeta = useMemo(() => {
    const config = {
      total: { title: 'All Chambers', rows: chamberListByCard.total },
      active: { title: 'Active Chambers', rows: chamberListByCard.active },
      inactive: { title: 'Decommissioned Chambers', rows: chamberListByCard.inactive },
      overdue: { title: 'Chambers Needing Attention', rows: chamberListByCard.attention },
      due30: { title: 'Due Within 30 Days Chambers', rows: chamberListByCard.due30 },
      upToDate: { title: 'Up To Date Chambers', rows: chamberListByCard.upToDate },
      statusUnknown: { title: 'Chambers With Unknown Status', rows: chamberListByCard.unknown },
    };
    return config[activeKpiModal] || { title: '', rows: [] };
  }, [activeKpiModal, chamberListByCard]);

  const modalColumns = useMemo(
    () => [
      {
        title: 'Chamber',
        dataIndex: 'imageUrl',
        key: 'image',
        width: 96,
        render: (value, record) => (
          <div
            className="kpi-table-thumb"
            style={{
              width: 44,
              height: 44,
              minWidth: 44,
              maxWidth: 44,
            }}
          >
            <img
              src={value}
              alt={record.name}
              className="kpi-table-image"
              style={{
                width: '100%',
                height: '100%',
                minWidth: '100%',
                maxWidth: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
              onError={(event) => {
                event.currentTarget.src = '/product_images/MineSAFE_Standard_Design.png';
              }}
            />
          </div>
        ),
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: 260,
        render: (value, record) => (
          <div className="kpi-table-name-cell">
            <div className="kpi-table-name">{value}</div>
            <div className="kpi-table-sub">{record.siteName} {record.tenantName ? `• ${record.tenantName}` : ''}</div>
          </div>
        ),
      },
      {
        title: 'Remaining days',
        dataIndex: 'serviceDaysLeft',
        key: 'remainingDays',
        width: 180,
        render: (days) => {
          const numericDays = toNumber(days, 0);
          const text = numericDays < 0 ? `${Math.abs(numericDays)} days overdue` : `${numericDays} days left`;
          const color = numericDays < 0 ? 'red' : numericDays <= 30 ? 'orange' : 'green';
          return (
            <Tag color={color} className="kpi-days-tag">
              {text}
            </Tag>
          );
        },
      },
      {
        title: 'Next Service Day',
        dataIndex: 'nextServiceDate',
        key: 'nextServiceDate',
        width: 140,
        render: (dateValue) => {
          const parsedDate = parseDateOrNow(dateValue);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isOverdue = parsedDate.getTime() < today.getTime();

          return (
            <span className={`kpi-next-service-date ${isOverdue ? 'overdue' : ''}`}>
              {format(parsedDate, 'dd/MM/yyyy')}
            </span>
          );
        },
      },
      {
        title: 'Reports',
        key: 'reports',
        width: 112,
        render: (_, record) => {
          const chamberId = firstDefined(record, ['id', 'chamberId'], null);

          return (
            <Button
              type="primary"
              size="small"
              className="kpi-view-reports-btn"
              disabled={!chamberId}
              onClick={() => {
                if (!chamberId) return;

                history.push({
                  pathname: `/chambers/${chamberId}/reports`,
                  state: {
                    chamberName: firstDefined(record, ['name', 'chamberName'], ''),
                  },
                });
              }}
            >
              Reports
            </Button>
          );
        },
      },
    ],
    [history],
  );

  const activeGasSensorMeta = useMemo(() => {
    const externalData = dashboardStats.gasSensorCards?.externalGasNode || emptyStats().gasSensorCards.externalGasNode;
    const auraData = dashboardStats.gasSensorCards?.auraFx || emptyStats().gasSensorCards.auraFx;

    const config = {
      externalGasNode: {
        title: 'External Gas Node',
        subtitle: `Applicable Chambers: ${externalData.applicableCount || 0}`,
        overdue: externalData.groups?.overdue || [],
        due30: externalData.groups?.due30 || [],
        upToDate: externalData.groups?.upToDate || [],
        noRecord: externalData.groups?.noRecord || [],
      },
      auraFx: {
        title: 'Aura FX',
        subtitle: `Active Chambers: ${auraData.applicableCount || 0}`,
        overdue: auraData.groups?.overdue || [],
        due30: auraData.groups?.due30 || [],
        upToDate: auraData.groups?.upToDate || [],
        noRecord: auraData.groups?.noRecord || [],
      },
    };

    return config[activeGasSensorModal] || null;
  }, [activeGasSensorModal, dashboardStats.gasSensorCards]);

  const activeGasSensorDeviceType = useMemo(() => {
    if (activeGasSensorModal === 'externalGasNode') return 'EXTERNAL_GAS_NODE';
    if (activeGasSensorModal === 'auraFx') return 'AURA_FX';
    return '';
  }, [activeGasSensorModal]);

  const renderSensorDueStatus = (value) => {
    const date = parseNullableDate(value);
    if (!date) return <span className="sensor-status muted">Not recorded</span>;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const formatted = format(date, 'dd/MM/yyyy');

    if (date.getTime() < today.getTime()) {
      return <span className="sensor-status overdue">{formatted}</span>;
    }

    return <span className="sensor-status ok">{formatted}</span>;
  };

  const getSensorDisplayMeta = (sensor, index) => {
    const rawSensorName = String(
      firstDefined(sensor, ['sensorName', 'name', 'sensor', 'sensorId'], `Sensor ${index + 1}`),
    ).trim();
    const gasTypeRaw = String(
      firstDefined(sensor, ['gasType', 'type', 'gas', 'sensorType'], 'UNKNOWN'),
    );
    const gasType = gasTypeRaw.replace(/_/g, ' ').toUpperCase();
    const gasImageKey = gasTypeRaw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() || 'OTHER';
    const isGenericName = /^sensor\s*\d+$/i.test(rawSensorName) || /^sensor$/i.test(rawSensorName);

    return {
      sensorName: rawSensorName,
      gasType,
      imageSrc: `/product_images/${gasImageKey}.png`,
      fallbackImageSrc: '/product_images/Other.png',
      displayLabel: isGenericName ? `${gasType} Sensor` : rawSensorName,
    };
  };

  const getSensorBooleanFlag = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      if (value.toLowerCase() === 'true') return true;
      if (value.toLowerCase() === 'false') return false;
    }
    return Boolean(value);
  };

  const selectGasSensorChamber = (row) => {
    if (!row || !activeGasSensorDeviceType) return;

    const chamberId = toNumber(firstDefined(row, ['id', 'chamberId'], 0), 0);
    if (!chamberId) return;

    setActiveGasSensorChamber(row);
    dispatch(fetchGasSensorExpiryLog({ chamberId, deviceType: activeGasSensorDeviceType }));
  };

  useEffect(() => {
    setActiveGasSensorChamber(null);
    dispatch(clearGasSensorExpiry());
  }, [activeGasSensorModal, dispatch]);

  const gasSensorDetailData = useMemo(() => {
    const payload = gasSensorExpiryData || {};
    const sensorsRaw = firstDefined(payload, ['sensors', 'data.sensors', 'payload.sensors'], []);
    const sensors = Array.isArray(sensorsRaw) ? sensorsRaw : [];
    const servicedDate = firstDefined(payload, ['servicedDate', 'lastServicedDate', 'serviceDate', 'data.servicedDate'], null);

    return {
      servicedDate,
      sensors,
    };
  }, [gasSensorExpiryData]);

  const activeConsumableMeta = useMemo(() => {
    const cards = dashboardStats.consumableCards || emptyStats().consumableCards;

    const config = {
      oxygenCandles: {
        title: 'Oxygen Candles',
        subtitle: `Applicable Chambers: ${cards.oxygenCandles.applicableCount || 0}`,
        overdue: cards.oxygenCandles.groups?.overdue || [],
        due30: cards.oxygenCandles.groups?.due30 || [],
        upToDate: cards.oxygenCandles.groups?.upToDate || [],
      },
      co2Cartridge: {
        title: 'CO2 Cartridge',
        subtitle: `Applicable Chambers: ${cards.co2Cartridge.applicableCount || 0}`,
        overdue: cards.co2Cartridge.groups?.overdue || [],
        due30: cards.co2Cartridge.groups?.due30 || [],
        upToDate: cards.co2Cartridge.groups?.upToDate || [],
      },
      gasTubes: {
        title: 'Gas Tubes',
        subtitle: `Applicable Chambers: ${cards.gasTubes.applicableCount || 0}`,
        overdue: cards.gasTubes.groups?.overdue || [],
        due30: cards.gasTubes.groups?.due30 || [],
        upToDate: cards.gasTubes.groups?.upToDate || [],
      },
      coCartridge: {
        title: 'CO Cartridge',
        subtitle: `Applicable Chambers: ${cards.coCartridge.applicableCount || 0}`,
        overdue: cards.coCartridge.groups?.overdue || [],
        due30: cards.coCartridge.groups?.due30 || [],
        upToDate: cards.coCartridge.groups?.upToDate || [],
      },
    };

    return config[activeConsumableModal] || null;
  }, [activeConsumableModal, dashboardStats.consumableCards]);

  useEffect(() => {
    setActiveConsumableChamber(null);
  }, [activeConsumableModal]);

  const selectedConsumableChamberData = useMemo(() => {
    if (!activeConsumableChamber) return null;

    const chambers = Array.isArray(dashboardStats.chambers) ? dashboardStats.chambers : [];
    const selectedId = String(firstDefined(activeConsumableChamber, ['id', 'chamberId'], ''));
    const selectedName = String(firstDefined(activeConsumableChamber, ['name', 'chamberName'], '')).toLowerCase();

    return (
      chambers.find((chamber) => String(firstDefined(chamber, ['id', 'chamberId'], '')) === selectedId) ||
      chambers.find((chamber) => String(firstDefined(chamber, ['name', 'chamberName'], '')).toLowerCase() === selectedName) ||
      null
    );
  }, [activeConsumableChamber, dashboardStats.chambers]);

  const consumableDetailRows = useMemo(() => {
    if (!selectedConsumableChamberData || !activeConsumableModal) return [];

    if (activeConsumableModal === 'oxygenCandles') {
      return [
        {
          key: 'oxygen-candle',
          itemName: 'Oxygen Candle',
          applicable: Boolean(selectedConsumableChamberData.oxygenCandleApplicable),
          expiryDate: selectedConsumableChamberData.oxygenCandleExpiry,
        },
      ];
    }

    if (activeConsumableModal === 'co2Cartridge') {
      return [
        {
          key: 'co2-cartridge',
          itemName: 'CO2 Cartridge',
          applicable: Boolean(selectedConsumableChamberData.co2CartridgeApplicable),
          expiryDate: selectedConsumableChamberData.co2CartridgeExpiry,
        },
      ];
    }

    if (activeConsumableModal === 'coCartridge') {
      return [
        {
          key: 'co-cartridge',
          itemName: 'CO Cartridge',
          applicable: Boolean(selectedConsumableChamberData.coCartridgeApplicable),
          expiryDate: selectedConsumableChamberData.coCartridgeExpiry,
        },
      ];
    }

    if (activeConsumableModal === 'gasTubes') {
      return [
        {
          key: 'o2-tube',
          itemName: 'O2 Tube',
          applicable: Boolean(selectedConsumableChamberData.o2TubeApplicable),
          expiryDate: selectedConsumableChamberData.o2TubeExpiry,
        },
        {
          key: 'co-tube',
          itemName: 'CO Tube',
          applicable: Boolean(selectedConsumableChamberData.coTubeApplicable),
          expiryDate: selectedConsumableChamberData.coTubeExpiry,
        },
        {
          key: 'co2-tube',
          itemName: 'CO2 Tube',
          applicable: Boolean(selectedConsumableChamberData.co2TubeApplicable),
          expiryDate: selectedConsumableChamberData.co2TubeExpiry,
        },
      ];
    }

    return [];
  }, [activeConsumableModal, selectedConsumableChamberData]);

  const faultDisplayRows = useMemo(() => {
    const faultRows = Array.isArray(dashboardStats.chamberFaultRows) ? dashboardStats.chamberFaultRows : [];
    const chambers = Array.isArray(dashboardStats.chambers) ? dashboardStats.chambers : [];
    const compareFaultPriority = (a, b) => {
      if (b.high !== a.high) return b.high - a.high;
      if (b.medium !== a.medium) return b.medium - a.medium;
      if (b.total !== a.total) return b.total - a.total;
      return String(a.chamberName).localeCompare(String(b.chamberName));
    };

    if (chambers.length === 0) {
      return faultRows
        .filter((row) => toNumber(row?.total, 0) > 0)
        .sort(compareFaultPriority);
    }

    const byId = new Map(
      faultRows.map((row) => [String(firstDefined(row, ['chamberId', 'id'], '')), row]),
    );
    const byName = new Map(
      faultRows.map((row) => [String(firstDefined(row, ['chamberName', 'name'], '')).toLowerCase(), row]),
    );

    return chambers
      .map((chamber, idx) => {
        const chamberId = String(firstDefined(chamber, ['id', 'chamberId'], `catalog-${idx}`));
        const chamberName = String(firstDefined(chamber, ['name', 'chamberName'], `Chamber ${idx + 1}`));
        const faultRow = byId.get(chamberId) || byName.get(chamberName.toLowerCase()) || null;

        const high = toNumber(firstDefined(faultRow, ['high'], 0), 0);
        const medium = toNumber(firstDefined(faultRow, ['medium'], 0), 0);
        const low = toNumber(firstDefined(faultRow, ['low'], 0), 0);
        const total = toNumber(firstDefined(faultRow, ['total'], high + medium + low), high + medium + low);

        return {
          chamberId,
          chamberName,
          siteId: firstDefined(faultRow, ['siteId', 'site.id', 'site_id', 'siteIdentifier'], firstDefined(chamber, ['siteId'], null)),
          siteName: firstDefined(faultRow, ['siteName', 'site.name'], firstDefined(chamber, ['siteName', 'site.name'], 'Unknown Site')),
          total,
          high,
          medium,
          low,
          status: high > 0 ? 'high' : medium > 0 ? 'attention' : 'healthy',
          highFaultItems: firstDefined(faultRow, ['highFaultItems'], []),
          mediumFaultItems: firstDefined(faultRow, ['mediumFaultItems'], []),
          lowFaultItems: firstDefined(faultRow, ['lowFaultItems'], []),
        };
      })
      .sort(compareFaultPriority)
      .filter((row) => row.high > 0 || row.medium > 0 || row.low > 0 || row.total > 0);
  }, [dashboardStats.chamberFaultRows, dashboardStats.chambers]);

  const siteScopedFaultRows = useMemo(() => {
    if (!isSiteRole) return faultDisplayRows;

    if (!Array.isArray(siteRoleScopedSites) || siteRoleScopedSites.length === 0) return [];

    const allowedSiteIds = new Set(
      siteRoleScopedSites
        .map((site) => normalizeSiteIdentifier(site?.id))
        .filter(Boolean),
    );
    const allowedSiteNames = new Set(
      siteRoleScopedSites
        .map((site) => normalizeSiteLabel(site?.name))
        .filter(Boolean),
    );

    return faultDisplayRows.filter((row) => {
      const rowSiteId = normalizeSiteIdentifier(row?.siteId);
      const rowSiteName = normalizeSiteLabel(row?.siteName);

      if (allowedSiteIds.size > 0) {
        if (rowSiteId) return allowedSiteIds.has(rowSiteId);
        if (allowedSiteNames.size > 0) return allowedSiteNames.has(rowSiteName);
        return false;
      }

      if (allowedSiteNames.size > 0) return allowedSiteNames.has(rowSiteName);
      return false;
    });
  }, [
    faultDisplayRows,
    isSiteRole,
    siteRoleScopedSites,
  ]);

  useEffect(() => {
    if (loading) return undefined;
    const table = faultTableRef.current;
    if (!table || typeof IntersectionObserver === 'undefined') return undefined;

    const rowsByKey = new Map(
      siteScopedFaultRows.map((row) => [
        getFaultSummaryKey(row, selectedTenant || userTenantId, selectedSite),
        row,
      ]),
    );

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(async (entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);

        const key = entry.target.dataset.faultSummaryKey;
        const row = rowsByKey.get(key);
        if (!row || faultSummaryRequestsRef.current.has(key)) return;

        const existingItems = [
          ...(row.highFaultItems || []),
          ...(row.mediumFaultItems || []),
          ...(row.lowFaultItems || []),
        ].filter((item) => item.question && item.question.trim());
        if (existingItems.length >= row.total) {
          setFaultSummaryById((current) => ({ ...current, [key]: row }));
          return;
        }

        faultSummaryRequestsRef.current.add(key);
        setFaultSummaryLoadingById((current) => ({ ...current, [key]: true }));

        try {
          const payload = await GET(CHAMBER_FAULTS_ENDPOINT(row.chamberId), {
            params: {
              ...((selectedTenant || userTenantId) ? { tenantId: selectedTenant || userTenantId } : {}),
            },
          });
          setFaultSummaryById((current) => ({
            ...current,
            [key]: normalizeFaultDetailsResponse(payload, row),
          }));
        } catch (err) {
          setFaultSummaryById((current) => ({ ...current, [key]: row }));
        } finally {
          setFaultSummaryLoadingById((current) => ({ ...current, [key]: false }));
        }
      });
    }, { root: table, threshold: 0.05 });

    table.querySelectorAll('[data-fault-summary-key]').forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, [loading, siteScopedFaultRows, selectedTenant, selectedSite, userTenantId]);

  return (
    <SubPageLayout
      title="Fleet overview"
      subtitle=""
      className="support-dashboard-layout"
      extra={(
        <div className="scope-controls">
          <div className="scope-field">
            <div className="scope-label">Company</div>
            {canSwitchTenant ? (
              <Select
                className="tenant-select"
                value={selectedTenant || undefined}
                options={tenantOptions}
                onChange={setSelectedTenant}
                placeholder="Select company"
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) =>
                  String(option?.label || '')
                    .toLowerCase()
                    .includes(String(input || '').toLowerCase())
                }
              />
            ) : (
              <div className="scope-chip"><ShopOutlined /> {selectedTenantLabel}</div>
            )}
          </div>
          {canFilterBySite && (
            <div className="scope-field">
              <div className="scope-label">Site</div>
              <Select
                className="site-select"
                value={selectedSite || undefined}
                options={siteOptions.map((site) => ({
                  value: String(site.id || site.name),
                  label: site.name || String(site.id || ''),
                }))}
                onChange={(value) => setSelectedSite(value || '')}
                placeholder="All sites"
                showSearch
                allowClear
                optionFilterProp="label"
                disabled={siteOptions.length === 0}
                filterOption={(input, option) =>
                  String(option?.label || '')
                    .toLowerCase()
                    .includes(String(input || '').toLowerCase())
                }
              />
            </div>
          )}
          {isSiteRole && !canSwitchTenant && (
            <div className="scope-chip"><ShopOutlined /> {selectedSiteLabel}</div>
          )}
        </div>
      )}
    >
      <div className="support-dashboard-page animate-in">
        {error && <Alert type="warning" showIcon message={error} style={{ marginBottom: 14 }} />}

        {loading ? (
          <LoadingDashboardSkeleton />
        ) : dashboardStats.total === 0 ? (
          <Empty description="No chambers in this scope." />
        ) : (
          <>
            <div className="kpi-grid">
              <div
                className="kpi-card kpi-card--active tilt-card kpi-clickable"
                onMouseMove={applyTiltFromPointer}
                onMouseLeave={resetTilt}
                onClick={() => setActiveKpiModal('active')}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') setActiveKpiModal('active');
                }}
              >
                <div className="kpi-content">
                  <div className="kpi-heading">
                    <span className="kpi-icon-badge"><AppstoreOutlined /></span>
                    <span className="kpi-title">Active chambers</span>
                  </div>
                  <div className="kpi-value">{activeChamberCount}</div>
                  <div className="kpi-sub">Across {activeSiteCount} {activeSiteCount === 1 ? 'site' : 'sites'}</div>
                </div>
              </div>

              <div
                className="kpi-card kpi-card--attention tilt-card kpi-clickable"
                onMouseMove={applyTiltFromPointer}
                onMouseLeave={resetTilt}
                onClick={() => setActiveKpiModal('overdue')}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') setActiveKpiModal('overdue');
                }}
              >
                <div className="kpi-content">
                  <div className="kpi-heading">
                    <span className="kpi-icon-badge"><AlertOutlined /></span>
                    <span className="kpi-title">Needs attention</span>
                  </div>
                  <div className="kpi-value">{needsAttentionCount}</div>
                  <div className="kpi-sub">
                    <span className="kpi-critical-count">{criticalChamberCount} critical</span>
                    <span className="kpi-sub-divider">·</span>
                    <span>{overdueChamberCount} overdue</span>
                  </div>
                </div>
              </div>

              <div
                className="kpi-card kpi-card--due tilt-card kpi-clickable"
                onMouseMove={applyTiltFromPointer}
                onMouseLeave={resetTilt}
                onClick={() => setActiveKpiModal('due30')}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') setActiveKpiModal('due30');
                }}
              >
                <div className="kpi-content">
                  <div className="kpi-heading">
                    <span className="kpi-icon-badge"><ClockCircleOutlined /></span>
                    <span className="kpi-title">Due within 30 days</span>
                  </div>
                  <div className="kpi-value">{dashboardStats.dueInMonth}</div>
                  <div className="kpi-sub">Plan upcoming maintenance</div>
                </div>
              </div>

              <div
                className="kpi-card kpi-card--decommissioned tilt-card kpi-clickable"
                onMouseMove={applyTiltFromPointer}
                onMouseLeave={resetTilt}
                onClick={() => setActiveKpiModal('inactive')}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') setActiveKpiModal('inactive');
                }}
              >
                <div className="kpi-content">
                  <div className="kpi-heading">
                    <span className="kpi-icon-badge"><StopOutlined /></span>
                    <span className="kpi-title">Decommissioned Chambers</span>
                  </div>
                  <div className="kpi-value">{dashboardStats.inactiveChambers}</div>
                  <div className="kpi-sub">Removed from active fleet</div>
                </div>
              </div>
            </div>

            <div className="panel chamber-faults-panel">
              <div className="panel-head"><h3>Chambers needing attention</h3></div>
              <div className="fault-table" ref={faultTableRef} role="table" aria-label="Chambers with faults">
                <div className="fault-table-head" role="row">
                  <span role="columnheader">Priority</span>
                  <span role="columnheader">Chamber</span>
                  <span role="columnheader">Site</span>
                  <span role="columnheader">Issue Summary</span>
                </div>
                <div className="fault-table-body">
                  {siteScopedFaultRows.length ? siteScopedFaultRows.map((row) => {
                    const summaryKey = getFaultSummaryKey(row, selectedTenant || userTenantId, selectedSite);
                    const summaryRow = faultSummaryById[summaryKey] || row;
                    const summaryItems = [
                      ...(summaryRow.highFaultItems || []).map((item) => ({ ...item, priority: 'critical' })),
                      ...(summaryRow.mediumFaultItems || []).map((item) => ({ ...item, priority: 'attention' })),
                      ...(summaryRow.lowFaultItems || []).map((item) => ({ ...item, priority: 'low' })),
                    ];
                    const visibleSummaryItems = summaryItems.slice(0, 2);
                    const priorityLabel = row.high > 0
                      ? 'Critical'
                      : row.medium > 0
                      ? 'Needs attention'
                      : 'Low priority';
                    const priorityTone = row.high > 0 ? 'critical' : row.medium > 0 ? 'attention' : 'low';

                    return (
                      <div className="fault-table-row" role="row" key={`${row.chamberId}-${row.chamberName}`}>
                        <div className="fault-priority-cell" role="cell">
                          <span className={`fault-priority-dot ${priorityTone}`} />
                          <span className="fault-priority-label">{priorityLabel}</span>
                          <span className="fault-priority-count">{row.high > 0 ? row.high : row.medium > 0 ? row.medium : row.low}</span>
                        </div>
                        <div className="fault-chamber-cell" role="cell">
                          <button type="button" className="fault-chamber-link" onClick={() => openFaultDetails(row)}>
                            {row.chamberName}
                          </button>
                        </div>
                        <div className="fault-site-cell" role="cell">{row.siteName || 'Unknown Site'}</div>
                        <div
                          className="fault-summary-cell"
                          role="cell"
                          data-fault-summary-key={summaryKey}
                        >
                          <div className="fault-summary-content">
                            {visibleSummaryItems.length ? (
                              <div className="fault-summary-items">
                                {visibleSummaryItems.map((item, index) => (
                                  <div className="fault-summary-item" key={`${row.chamberId}-summary-${index}`}>
                                    <span className={`fault-priority-dot ${item.priority}`} />
                                    <span>{item.question || 'Fault details unavailable'}</span>
                                  </div>
                                ))}
                              </div>
                            ) : faultSummaryLoadingById[summaryKey] ? (
                              <span className="fault-summary-fallback">Loading issue details...</span>
                            ) : (
                              <span className="fault-summary-fallback">Issue details are available in the chamber record</span>
                            )}
                            {summaryItems.length > visibleSummaryItems.length && visibleSummaryItems.length > 0 && (
                              <span className="fault-summary-more">
                                {summaryItems.length - visibleSummaryItems.length} more in details
                              </span>
                            )}
                          </div>
                          <Button className="view-chamber-button" onClick={() => openFaultDetails(row)}>
                            View Chamber <span aria-hidden="true">›</span>
                          </Button>
                        </div>
                      </div>
                    );
                  }) : (
                    <div className="fault-table-empty">No chambers with faults in this scope.</div>
                  )}
                </div>
              </div>
            </div>

            <div className="insight-grid">
              <div className="panel gas-panel tilt-card" onMouseMove={applyTiltFromPointer} onMouseLeave={resetTilt}>
                <div className="panel-head"><h3>Sensors</h3></div>
                <div className="gas-family-grid">
                  {dashboardStats.gasSensorCards.externalGasNode.applicableCount > 0 && (
                  <div
                    className="gas-family-card gas-family-card--external"
                    role="button"
                    tabIndex={0}
                    onClick={() => setActiveGasSensorModal('externalGasNode')}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') setActiveGasSensorModal('externalGasNode');
                    }}
                  >
                    <div className="sub-card-head">
                      <div className="gas-family-title">External Gas Node</div>
                      <span className="gas-family-count">{dashboardStats.gasSensorCards.externalGasNode.applicableCount} chambers</span>
                    </div>
                    <div className="gas-status-grid">
                      {[
                        { key: 'overdue', label: 'Overdue', value: dashboardStats.gasSensorCards.externalGasNode.overdue },
                        { key: 'due30', label: 'Due within 30 days', value: dashboardStats.gasSensorCards.externalGasNode.due30 },
                        { key: 'current', label: 'Up to date', value: dashboardStats.gasSensorCards.externalGasNode.stable },
                        { key: 'noRecord', label: 'No records', value: dashboardStats.gasSensorCards.externalGasNode.noRecord },
                      ].map((item) => (
                        <div className={`gas-status-item ${item.key}`} key={`external-${item.key}`}>
                          <span className="gas-status-indicator" />
                          <span className="gas-status-label">{item.label}</span>
                          <strong>{item.value}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}
                  <div
                    className="gas-family-card gas-family-card--aura"
                    role="button"
                    tabIndex={0}
                    onClick={() => setActiveGasSensorModal('auraFx')}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') setActiveGasSensorModal('auraFx');
                    }}
                  >
                    <div className="sub-card-head">
                      <div className="gas-family-title">Aura FX</div>
                      <span className="gas-family-count">{dashboardStats.gasSensorCards.auraFx.applicableCount} chambers</span>
                    </div>
                    <div className="gas-status-grid">
                      {[
                        { key: 'overdue', label: 'Overdue', value: dashboardStats.gasSensorCards.auraFx.overdue },
                        { key: 'due30', label: 'Due within 30 days', value: dashboardStats.gasSensorCards.auraFx.due30 },
                        { key: 'current', label: 'Up to date', value: dashboardStats.gasSensorCards.auraFx.stable },
                        { key: 'noRecord', label: 'No records', value: dashboardStats.gasSensorCards.auraFx.noRecord },
                      ].map((item) => (
                        <div className={`gas-status-item ${item.key}`} key={`aura-${item.key}`}>
                          <span className="gas-status-indicator" />
                          <span className="gas-status-label">{item.label}</span>
                          <strong>{item.value}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="panel replacement-panel tilt-card" onMouseMove={applyTiltFromPointer} onMouseLeave={resetTilt}>
                <div className="panel-head"><h3>Consumables</h3></div>
                <div className="replacement-grid">
                  {[
                    {
                      key: 'oxygenCandles',
                      // icon: <FireOutlined />,
                      title: 'Oxygen Candles',
                      card: dashboardStats.consumableCards.oxygenCandles,
                    },
                    {
                      key: 'co2Cartridge',
                      // icon: <ThunderboltOutlined />,
                      title: 'CO2 Cartridge',
                      card: dashboardStats.consumableCards.co2Cartridge,
                    },
                    {
                      key: 'gasTubes',
                      // icon: <AlertOutlined />,
                      title: 'Gas Tubes',
                      card: dashboardStats.consumableCards.gasTubes,
                    },
                    {
                      key: 'coCartridge',
                      // icon: <ShopOutlined />,
                      title: 'CO Cartridge',
                      card: dashboardStats.consumableCards.coCartridge,
                    },
                  ].map((item) => (
                    <div
                      key={item.key}
                      className="rep-card rep-card-clickable"
                      role="button"
                      tabIndex={0}
                      onClick={() => setActiveConsumableModal(item.key)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') setActiveConsumableModal(item.key);
                      }}
                    >
                      <div className="sub-card-head"><div className="gas-family-title">{item.title}</div></div>
                      <div className="gas-status-grid">
                        {[
                          { key: 'overdue', label: 'Overdue', value: item.card?.overdue || 0 },
                          { key: 'current', label: 'Up to date', value: item.card?.upToDate || 0 },
                          { key: 'due30', label: 'Due within 30 days', value: item.card?.due30 || 0 },
                          { key: 'noRecord', label: 'No records', value: item.card?.noRecord || 0 },
                        ].map((status) => (
                          <div className={`gas-status-item ${status.key}`} key={`${item.key}-${status.key}`}>
                            <span className="gas-status-indicator" />
                            <span className="gas-status-label">{status.label}</span>
                            <strong>{status.value}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </>
        )}

        <Modal
          title={`${modalMeta.title} (${modalMeta.rows.length})`}
          visible={Boolean(activeKpiModal)}
          footer={null}
          onCancel={() => setActiveKpiModal('')}
          width={900}
          wrapClassName="support-kpi-modal-wrap"
          destroyOnClose
        >
          <div className="kpi-modal-table-wrap">
            <Table
              rowKey={(row) => `${row.id}-${row.name}`}
              className="kpi-modal-table"
              columns={modalColumns}
              dataSource={modalMeta.rows}
              pagination={modalMeta.rows.length > 8 ? { pageSize: 8, showSizeChanger: false } : false}
              scroll={modalMeta.rows.length > 8 ? { y: 420 } : undefined}
              locale={{ emptyText: <Empty description="No chambers for this category." /> }}
            />
          </div>
        </Modal>

        <Modal
          title={activeGasSensorMeta ? `${activeGasSensorMeta.title} Sensor Status` : 'Gas Sensor Status'}
          visible={Boolean(activeGasSensorModal)}
          footer={null}
          onCancel={() => {
            setActiveGasSensorModal('');
            setActiveGasSensorChamber(null);
            dispatch(clearGasSensorExpiry());
          }}
          width={900}
          wrapClassName="support-gas-modal-wrap"
          destroyOnClose
        >
          {activeGasSensorMeta && (
            <div className="gas-modal-content">
              <div className="gas-modal-subtitle">{activeGasSensorMeta.subtitle}</div>
              <div className="gas-modal-grid">
                {[
                  { key: 'overdue', title: 'Overdue', rows: activeGasSensorMeta.overdue, tone: 'overdue' },
                  { key: 'due30', title: 'Due in 30 Days', rows: activeGasSensorMeta.due30, tone: 'due30' },
                  { key: 'upToDate', title: 'Up To Date', rows: activeGasSensorMeta.upToDate, tone: 'upToDate' },
                  { key: 'noRecord', title: 'No Sensor records', rows: activeGasSensorMeta.noRecord, tone: 'noRecord' },
                ].map((group) => (
                  <section className={`gas-modal-column ${group.tone}`} key={group.key}>
                    <div className="gas-modal-column-head">
                      <h4>{group.title}</h4>
                      <Tag>{group.rows.length}</Tag>
                    </div>
                    {group.rows.length === 0 ? (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chambers" />
                    ) : (
                      <div className="gas-modal-list">
                        {group.rows.map((row) => (
                          <div
                            className={`gas-modal-row gas-modal-row-clickable ${
                              String(firstDefined(activeGasSensorChamber, ['id', 'chamberId'], '')) ===
                              String(firstDefined(row, ['id', 'chamberId'], ''))
                                ? 'is-active'
                                : ''
                            }`}
                            key={`${group.key}-${row.id}-${row.name}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => selectGasSensorChamber(row)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') selectGasSensorChamber(row);
                            }}
                          >
                            <img
                              src={row.imageUrl || '/product_images/MineSAFE_Standard_Design.png'}
                              alt={row.name}
                              className="gas-modal-thumb"
                              onError={(event) => {
                                event.currentTarget.src = '/product_images/MineSAFE_Standard_Design.png';
                              }}
                            />
                            <div className="gas-modal-row-main">
                              <div className="gas-modal-row-name">{row.name}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                ))}
              </div>

              <div className="gas-sensor-detail-panel">
                <div className="gas-sensor-detail-head">
                  <div className="gas-sensor-detail-title">
                    {activeGasSensorChamber
                      ? `Sensor Data - ${activeGasSensorChamber.name}`
                      : 'Sensor Data'}
                  </div>
                  {activeGasSensorChamber && (
                    <div className="gas-sensor-detail-actions">
                      <div className="gas-sensor-detail-service">
                        Last service:{' '}
                        {(() => {
                          const servicedDate = parseNullableDate(gasSensorDetailData.servicedDate);
                          return servicedDate ? format(servicedDate, 'dd/MM/yyyy') : 'Not recorded';
                        })()}
                      </div>
                      <Button
                        type="primary"
                        className="gas-view-reports-btn"
                        onClick={goToGasChamberReports}
                        disabled={!firstDefined(activeGasSensorChamber, ['chamberId', 'id'], null)}
                      >
                        View Reports
                      </Button>
                    </div>
                  )}
                </div>

                {!activeGasSensorChamber ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Select a chamber to view sensor data"
                  />
                ) : gasSensorExpiryLoading ? (
                  <div className="gas-sensor-loading-wrap">
                    <Spin size="small" />
                  </div>
                ) : gasSensorExpiryError ? (
                  <Alert type="warning" showIcon message={gasSensorExpiryError} />
                ) : gasSensorDetailData.sensors.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No sensor data recorded for this chamber"
                  />
                ) : (
                  <div className="gas-sensor-table-wrap">
                    <table className="gas-sensor-table">
                      <thead>
                        <tr>
                          <th>Sensor</th>
                          <th>Gas Type</th>
                          <th>Expiry Date</th>
                          <th>Calibration Due</th>
                          <th>Calibrated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gasSensorDetailData.sensors.map((sensor, index) => {
                          const sensorMeta = getSensorDisplayMeta(sensor, index);
                          const expiryDate = firstDefined(sensor, ['expiryDate', 'expiryDateEpoch', 'expiryDateMillis', 'expiry'], null);
                          const calibrationDueDate = firstDefined(
                            sensor,
                            ['calibrationDueDate', 'calibrationDueDateEpoch', 'calibrationDueDateMillis', 'calibrationDue'],
                            null,
                          );
                          const calibrated = getSensorBooleanFlag(
                            firstDefined(sensor, ['calibrated', 'isCalibrated'], null),
                          );

                          return (
                            <tr key={`${sensorMeta.sensorName}-${sensorMeta.gasType}-${index}`}>
                              <td>
                                <div className="sensor-name-chip">
                                  <div className="sensor-name-chip-image-wrap">
                                    <img
                                      src={sensorMeta.imageSrc}
                                      alt={`${sensorMeta.gasType} sensor`}
                                      className="sensor-name-chip-image"
                                      onError={(event) => {
                                        if (!event.currentTarget.src.endsWith('/product_images/Other.png')) {
                                          event.currentTarget.src = sensorMeta.fallbackImageSrc;
                                        }
                                      }}
                                    />
                                  </div>
                                </div>
                              </td>
                              <td>{sensorMeta.gasType}</td>
                              <td>{renderSensorDueStatus(expiryDate)}</td>
                              <td>{renderSensorDueStatus(calibrationDueDate)}</td>
                              <td>
                                {calibrated === null ? (
                                  <span className="sensor-status muted">-</span>
                                ) : calibrated ? (
                                  <span className="sensor-status ok">✓</span>
                                ) : (
                                  <span className="sensor-status overdue">✗</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal>

        <Modal
          title={activeConsumableMeta ? `${activeConsumableMeta.title} Chambers` : 'Consumable Chambers'}
          visible={Boolean(activeConsumableModal)}
          footer={null}
          onCancel={() => {
            setActiveConsumableModal('');
            setActiveConsumableChamber(null);
          }}
          width={900}
          wrapClassName="support-consumable-modal-wrap"
          destroyOnClose
        >
          {activeConsumableMeta && (
            <div className="consumable-modal-content">
              <div className="consumable-modal-subtitle">{activeConsumableMeta.subtitle}</div>
              <div className="consumable-modal-grid">
                {[
                  { key: 'overdue', title: 'Overdue', rows: activeConsumableMeta.overdue, tone: 'overdue' },
                  { key: 'due30', title: 'Due in 30 Days', rows: activeConsumableMeta.due30, tone: 'due30' },
                  { key: 'upToDate', title: 'Up To Date', rows: activeConsumableMeta.upToDate, tone: 'upToDate' },
                ].map((group) => (
                  <section className={`consumable-modal-column ${group.tone}`} key={group.key}>
                    <div className="consumable-modal-column-head">
                      <h4>{group.title}</h4>
                      <Tag>{group.rows.length}</Tag>
                    </div>
                    {group.rows.length === 0 ? (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chambers" />
                    ) : (
                      <div className="consumable-modal-list">
                        {group.rows.map((row) => (
                          <div
                            className={`consumable-modal-row consumable-modal-row-clickable ${
                              String(firstDefined(activeConsumableChamber, ['id', 'chamberId'], '')) ===
                              String(firstDefined(row, ['id', 'chamberId'], ''))
                                ? 'is-active'
                                : ''
                            }`}
                            key={`${group.key}-${row.id}-${row.name}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => setActiveConsumableChamber(row)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') setActiveConsumableChamber(row);
                            }}
                          >
                            <img
                              src={row.imageUrl || '/product_images/MineSAFE_Standard_Design.png'}
                              alt={row.name}
                              className="consumable-modal-thumb"
                              onError={(event) => {
                                event.currentTarget.src = '/product_images/MineSAFE_Standard_Design.png';
                              }}
                            />
                            <div className="consumable-modal-row-name">{row.name}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                ))}
              </div>

              <div className="consumable-detail-panel">
                <div className="consumable-detail-head">
                  <div className="consumable-detail-title">
                    {activeConsumableChamber
                      ? `Expiry Details - ${activeConsumableChamber.name}`
                      : 'Expiry Details'}
                  </div>
                  {activeConsumableChamber && (
                    <Button
                      type="primary"
                      className="consumable-view-reports-btn"
                      onClick={goToConsumableChamberReports}
                      disabled={!firstDefined(activeConsumableChamber, ['chamberId', 'id'], null)}
                    >
                      View Reports
                    </Button>
                  )}
                </div>

                {!activeConsumableChamber ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Select a chamber to view expiry details"
                  />
                ) : consumableDetailRows.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No consumable expiry details available"
                  />
                ) : (
                  <div className="consumable-detail-table-wrap">
                    <table className="consumable-detail-table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Applicable</th>
                          <th>Expiry Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {consumableDetailRows.map((item) => (
                          <tr key={item.key}>
                            <td>{item.itemName}</td>
                            <td>
                              {item.applicable ? (
                                <span className="sensor-status ok">Yes</span>
                              ) : (
                                <span className="sensor-status muted">No</span>
                              )}
                            </td>
                            <td>
                              {item.applicable ? (
                                renderSensorDueStatus(item.expiryDate)
                              ) : (
                                <span className="sensor-status muted">Not applicable</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal>

        <Modal
          title={faultModalSelection ? `${faultModalSelection.chamberName} Fault Details` : 'Fault Details'}
          visible={Boolean(faultModalSelection)}
          onCancel={() => {
            setActiveFaultChamber(null);
            setFaultModalSelection(null);
            setFaultModalError('');
            setFaultModalLoading(false);
            setFaultModalApiResponse(null);
          }}
          footer={null}
          width={900}
          wrapClassName="support-fault-modal-wrap"
          destroyOnClose
        >
          {faultModalError && (
            <Alert
              type="warning"
              showIcon
              message={faultModalError}
              style={{ marginBottom: 10 }}
            />
          )}

          {faultModalLoading && (
            <div style={{ padding: '4px 0 12px' }}>
              <Spin size="small" />
            </div>
          )}

          {activeFaultChamber?.apiReturnedExplicitZeros && !faultModalLoading && !faultModalError && (
            <Alert
              type="info"
              showIcon
              message="The API returned no detailed fault items for this chamber. The modal is showing the response exactly as received."
              style={{ marginBottom: 10 }}
            />
          )}

          {!faultModalLoading && activeFaultChamber && (
            <div className="fault-modal-content">
              <div className="fault-modal-head">
                <div className="fault-modal-head-main">
                  <img src="/product_images/MineSAFE_Standard_Design.png" alt="Chamber" className="fault-modal-image" />
                  <div className="fault-modal-head-text">
                    <div className="fault-modal-chamber-name">{activeFaultChamber.chamberName}</div>
                    <div className="fault-modal-meta">{activeFaultChamber.siteName || 'Unknown Site'}</div>
                  </div>
                </div>
                <Button
                  type="primary"
                  className="fault-view-reports-btn"
                  onClick={goToFaultChamberReports}
                  disabled={!firstDefined(activeFaultChamber, ['chamberId', 'id'], null)}
                >
                  View Reports
                </Button>
              </div>

              <div className="fault-priority-grid">
                <div className="fault-priority-card high">
                  <div className="fault-priority-title">High Priorities ({activeFaultChamber.high || 0})</div>
                  <ul>
                    {(activeFaultChamber.highFaultItems || []).length > 0 ? (
                      activeFaultChamber.highFaultItems.map((item, idx) => (
                        <li key={`h-${idx}`}>
                          <div className="fault-item-question">{item.question || 'N/A'}</div>
                        </li>
                      ))
                    ) : (
                      <li>None</li>
                    )}
                  </ul>
                </div>

                <div className="fault-priority-card medium">
                  <div className="fault-priority-title">Medium Priorities ({activeFaultChamber.medium || 0})</div>
                  <ul>
                    {(activeFaultChamber.mediumFaultItems || []).length > 0 ? (
                      activeFaultChamber.mediumFaultItems.map((item, idx) => (
                        <li key={`m-${idx}`}>
                          <div className="fault-item-question">{item.question || 'N/A'}</div>
                        </li>
                      ))
                    ) : (
                      <li>None</li>
                    )}
                  </ul>
                </div>

                <div className="fault-priority-card low">
                  <div className="fault-priority-title">Low Priorities ({activeFaultChamber.low || 0})</div>
                  <ul>
                    {(activeFaultChamber.lowFaultItems || []).length > 0 ? (
                      activeFaultChamber.lowFaultItems.map((item, idx) => (
                        <li key={`l-${idx}`}>
                          <div className="fault-item-question">{item.question || 'N/A'}</div>
                        </li>
                      ))
                    ) : (
                      <li>None</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </SubPageLayout>
  );
};

export default SupportDashboard;
